import L from "leaflet";
import { PORTRAYAL_PACKS, createPortrayalEngine, registerPortrayalPack } from "./portrayal";
import { loadPortrayalPackFromUrl } from "./portrayal-loader";
import { getTemplateLibrary, listTemplateLibraryOptions, sanitizeTemplateLibraryId } from "./template-libraries";

const STATE_KEY = "hydrotools:enc-charts:v1";
const ENC_VECTOR_SERVICE_BASE = "https://encdirect.noaa.gov/arcgis/rest/services/encdirect/enc_harbour/MapServer";
const ENC_VECTOR_DEBOUNCE_MS = 600;

const ENC_LAYER_OVERRIDES = [
  { serviceId: 11, s52Role: "lights", minZoom: 8, defaultVisible: false, label: "Lights" },
  { serviceId: 227, s52Role: "depths", minZoom: 7, defaultVisible: false, label: "Depth areas" },
  { serviceId: 83, s52Role: "coastline", minZoom: 4, defaultVisible: false, label: "Coastline" },
  { serviceId: 131, s52Role: "routes", minZoom: 7, defaultVisible: false, label: "Tracks & routes" },
  { serviceId: 185, s52Role: "regulated", minZoom: 8, defaultVisible: false, label: "Regulated areas" },
  { serviceId: 76, s52Role: "soundings", minZoom: 10, defaultVisible: false, label: "Soundings", depthField: "Z" },
];

const ENC_LAYER_PRESETS = [
  { id: "none", label: "Custom / None", serviceIds: [] },
  { id: "nav_safety", label: "Navigation - Safety", serviceIds: [11, 76, 185, 227] },
  { id: "planning", label: "Survey Planning", serviceIds: [227, 83, 131] },
  { id: "symbology_test", label: "Symbology Test", serviceIds: [1, 4, 6, 11, 33, 36, 76, 84, 104, 131, 185, 227] },
];

const PORTRAYAL_XML_PRESETS = [
  { label: "portrayal_catalogue.xml", url: "https://raw.githubusercontent.com/iho-ohi/S-101_Portrayal-Catalogue/main/PortrayalCatalog/portrayal_catalogue.xml" },
  { label: "AlertCatalog-S101.xml", url: "https://raw.githubusercontent.com/iho-ohi/S-101_Portrayal-Catalogue/main/PortrayalCatalog/AlertCatalog-S101.xml" },
  { label: "colorProfile.xml", url: "https://raw.githubusercontent.com/iho-ohi/S-101_Portrayal-Catalogue/main/PortrayalCatalog/ColorProfiles/colorProfile.xml" },
  { label: "ACHARE51.xml", url: "https://raw.githubusercontent.com/iho-ohi/S-101_Portrayal-Catalogue/main/PortrayalCatalog/LineStyles/ACHARE51.xml" },
  { label: "DQUALA11.xml", url: "https://raw.githubusercontent.com/iho-ohi/S-101_Portrayal-Catalogue/main/PortrayalCatalog/AreaFills/DQUALA11.xml" },
];

const LINE_TYPE_TO_DASH = {
  solid: "",
  dashed: "8 6",
  dotted: "2 6",
};

const DEFAULT_DEPTH_BANDS = [
  { id: "b1", max: 2, color: "#c7e9ff" },
  { id: "b2", max: 5, color: "#95d5ff" },
  { id: "b3", max: 10, color: "#63bdff" },
  { id: "b4", max: 20, color: "#3b9ce6" },
  { id: "b5", max: null, color: "#1f6fb8" },
];

const DEFAULT_LIGHT_COLOR_MAP = {
  1: "#f8fafc", // white
  3: "#ef4444", // red
  4: "#22c55e", // green
  6: "#fbbf24", // yellow
};

const LIGHT_COLOR_LABELS = {
  1: "White (1)",
  3: "Red (3)",
  4: "Green (4)",
  6: "Yellow (6)",
};

const LIGHT_SYMBOLOGY_MODES = {
  simple: "simple",
  enc_icon: "enc_icon",
};

const POINT_SYMBOLOGY_MODES = {
  dot: "dot",
  nautical: "nautical",
};

function normalizeDash(dash) {
  return String(dash || "")
    .split(/[,\s]+/)
    .filter(Boolean)
    .join(" ");
}

function inferLineTypeFromDash(dash) {
  const n = normalizeDash(dash);
  if (!n) return "solid";
  if (n === normalizeDash(LINE_TYPE_TO_DASH.dotted)) return "dotted";
  return "dashed";
}

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");
}

function readState() {
  const fallback = {
    opacity: 0.9,
    autoRefresh: true,
    maxPerLayer: 1500,
    enabledLayerIds: [],
    presetId: "none",
    layerStyles: {},
    depthBands: DEFAULT_DEPTH_BANDS,
    lightColorMap: DEFAULT_LIGHT_COLOR_MAP,
    lightSymbologyMode: LIGHT_SYMBOLOGY_MODES.enc_icon,
    pointSymbologyMode: POINT_SYMBOLOGY_MODES.nautical,
    portrayalPackId: "enc-v0.1",
    portrayalCatalogUrl: "",
    soundingDotStartZoom: 15,
    controlsOpen: true,
    activeLayersOpen: true,
    availableLayersOpen: true,
    userChartsOpen: true,
    exportOpen: false,
    userPresets: [],
    selectedUserPreset: "",
    exportMode: "bounds",
    layerFilterText: "",
    s57ExportUrl: "http://localhost:8081/v1/export/s57",
    s57CellName: "US5TST01",
    s57Edition: 1,
    s57UpdateNumber: 0,
    userCharts: [],
    selectedUserChartId: "",
    templateLibraryId: "noaa_hssd_2026",
    userChartDraftClass: "S57_GENERIC",
    featureTableDock: "bottom",
    featureTableOpen: true,
  };
  try {
    const raw = localStorage.getItem(STATE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return {
      ...fallback,
      ...(parsed && typeof parsed === "object" ? parsed : {}),
      opacity: Number.isFinite(Number(parsed?.opacity)) ? Math.max(0.05, Math.min(1, Number(parsed.opacity))) : fallback.opacity,
      autoRefresh: parsed?.autoRefresh !== false,
      maxPerLayer: Number.isFinite(Number(parsed?.maxPerLayer)) ? Math.max(200, Math.min(5000, Number(parsed.maxPerLayer))) : fallback.maxPerLayer,
      enabledLayerIds: Array.isArray(parsed?.enabledLayerIds) ? parsed.enabledLayerIds.map((x) => Number(x)).filter((x) => Number.isFinite(x)) : [],
      presetId: typeof parsed?.presetId === "string" ? parsed.presetId : "none",
      layerStyles: parsed?.layerStyles && typeof parsed.layerStyles === "object" ? parsed.layerStyles : {},
      depthBands: sanitizeDepthBands(parsed?.depthBands),
      lightColorMap: sanitizeLightColorMap(parsed?.lightColorMap),
      lightSymbologyMode: sanitizeLightSymbologyMode(parsed?.lightSymbologyMode),
      pointSymbologyMode: sanitizePointSymbologyMode(parsed?.pointSymbologyMode),
      portrayalPackId: sanitizePortrayalPackId(parsed?.portrayalPackId),
      portrayalCatalogUrl: typeof parsed?.portrayalCatalogUrl === "string" ? parsed.portrayalCatalogUrl : "",
      soundingDotStartZoom: Number.isFinite(Number(parsed?.soundingDotStartZoom)) ? Math.max(6, Math.min(18, Number(parsed.soundingDotStartZoom))) : fallback.soundingDotStartZoom,
      controlsOpen: parsed?.controlsOpen !== false,
      activeLayersOpen: parsed?.activeLayersOpen !== false,
      availableLayersOpen: parsed?.availableLayersOpen !== false,
      userChartsOpen: parsed?.userChartsOpen !== false,
      exportOpen: parsed?.exportOpen === true,
      userPresets: Array.isArray(parsed?.userPresets) ? parsed.userPresets.filter((p) => p && typeof p === "object" && typeof p.name === "string" && p.data && typeof p.data === "object").slice(0, 40) : [],
      selectedUserPreset: typeof parsed?.selectedUserPreset === "string" ? parsed.selectedUserPreset : "",
      exportMode: parsed?.exportMode === "polygon" ? "polygon" : "bounds",
      layerFilterText: typeof parsed?.layerFilterText === "string" ? parsed.layerFilterText : "",
      s57ExportUrl: typeof parsed?.s57ExportUrl === "string" && parsed.s57ExportUrl.trim()
        ? parsed.s57ExportUrl.trim()
        : fallback.s57ExportUrl,
      s57CellName: typeof parsed?.s57CellName === "string" && parsed.s57CellName.trim()
        ? parsed.s57CellName.trim().slice(0, 8)
        : fallback.s57CellName,
      s57Edition: Number.isFinite(Number(parsed?.s57Edition)) ? Math.max(1, Math.min(999, Number(parsed.s57Edition))) : fallback.s57Edition,
      s57UpdateNumber: Number.isFinite(Number(parsed?.s57UpdateNumber)) ? Math.max(0, Math.min(9999, Number(parsed.s57UpdateNumber))) : fallback.s57UpdateNumber,
      userCharts: sanitizeUserCharts(parsed?.userCharts),
      selectedUserChartId: typeof parsed?.selectedUserChartId === "string" ? parsed.selectedUserChartId : "",
      templateLibraryId: sanitizeTemplateLibraryId(parsed?.templateLibraryId),
      userChartDraftClass: typeof parsed?.userChartDraftClass === "string" && parsed.userChartDraftClass.trim()
        ? parsed.userChartDraftClass.trim().slice(0, 64)
        : fallback.userChartDraftClass,
      featureTableDock: parsed?.featureTableDock === "panel" ? "panel" : "bottom",
      featureTableOpen: parsed?.featureTableOpen !== false,
    };
  } catch {
    return fallback;
  }
}

function sanitizeDepthBands(raw) {
  const bands = Array.isArray(raw) ? raw : DEFAULT_DEPTH_BANDS;
  const cleaned = bands
    .map((b, idx) => {
      const max = b?.max === null ? null : Number(b?.max);
      return {
        id: String(b?.id || `b${idx + 1}`),
        max: max === null || Number.isFinite(max) ? max : null,
        color: typeof b?.color === "string" ? b.color : DEFAULT_DEPTH_BANDS[Math.min(idx, DEFAULT_DEPTH_BANDS.length - 1)].color,
      };
    })
    .filter((b) => b.max === null || Number.isFinite(b.max));
  if (!cleaned.length) return [...DEFAULT_DEPTH_BANDS];
  cleaned.sort((a, b) => {
    if (a.max === null) return 1;
    if (b.max === null) return -1;
    return a.max - b.max;
  });
  if (cleaned[cleaned.length - 1].max !== null) cleaned.push({ id: `b${cleaned.length + 1}`, max: null, color: "#1f6fb8" });
  return cleaned;
}

function sanitizeLightColorMap(raw) {
  const src = raw && typeof raw === "object" ? raw : DEFAULT_LIGHT_COLOR_MAP;
  const out = {};
  for (const [k, v] of Object.entries(src)) {
    const code = String(k).trim();
    if (!/^\d+$/.test(code)) continue;
    if (typeof v !== "string" || !v) continue;
    out[code] = v;
  }
  for (const [k, v] of Object.entries(DEFAULT_LIGHT_COLOR_MAP)) {
    if (!out[String(k)]) out[String(k)] = v;
  }
  return out;
}

function sanitizeLightSymbologyMode(raw) {
  const v = String(raw || "").trim();
  return Object.values(LIGHT_SYMBOLOGY_MODES).includes(v) ? v : LIGHT_SYMBOLOGY_MODES.enc_icon;
}

function sanitizePointSymbologyMode(raw) {
  const v = String(raw || "").trim();
  return Object.values(POINT_SYMBOLOGY_MODES).includes(v) ? v : POINT_SYMBOLOGY_MODES.nautical;
}

function sanitizePortrayalPackId(raw) {
  const id = String(raw || "").trim();
  return PORTRAYAL_PACKS[id] ? id : "enc-v0.1";
}

function sanitizeUserCharts(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((c, idx) => {
      const id = String(c?.id || `chart-${idx + 1}`).trim() || `chart-${idx + 1}`;
      const name = String(c?.name || `User Chart ${idx + 1}`).trim() || `User Chart ${idx + 1}`;
      const visible = c?.visible !== false;
      const features = Array.isArray(c?.features)
        ? c.features
          .map((f, fIdx) => {
            if (!f || typeof f !== "object" || !f.geometry || typeof f.geometry !== "object") return null;
            const copy = deepClone(f);
            if (!copy) return null;
            copy.id = String(copy.id || `feature-${idx + 1}-${fIdx + 1}`);
            copy.type = "Feature";
            copy.properties = copy.properties && typeof copy.properties === "object" ? copy.properties : {};
            return copy;
          })
          .filter(Boolean)
        : [];
      return { id, name, visible, features };
    })
    .slice(0, 60);
}

function saveState(state) {
  try { localStorage.setItem(STATE_KEY, JSON.stringify(state)); } catch {}
}

function inferTypeHint(geometryType = "", layerName = "") {
  const n = String(layerName || "").toLowerCase().trim();
  if (/_point$/.test(n) || /\bpoint$/.test(n) || /p$/.test(n)) return "point";
  if (/_line$/.test(n) || /\bline$/.test(n) || /l$/.test(n)) return "line";
  if (/_area$/.test(n) || /\barea$/.test(n) || /\bpolygon$/.test(n) || /a$/.test(n)) return "polygon";

  const t = String(geometryType || "").toLowerCase();
  if (t.includes("point")) return "point";
  if (t.includes("polyline") || t.includes("line")) return "line";
  if (t.includes("polygon") || t.includes("area")) return "polygon";
  return "unknown";
}

function inferRole(name = "", typeHint = "") {
  const n = String(name || "").toLowerCase();
  if (n.includes("light")) return "lights";
  if (n.includes("sound") || n.includes("depth point") || n.includes("valsou")) return "soundings";
  if (n.includes("depth") || n.includes("drg") || n.includes("contour")) return "depths";
  if (n.includes("coast") || n.includes("shore")) return "coastline";
  if (n.includes("route") || n.includes("track") || n.includes("fairway")) return "routes";
  if (n.includes("regulat") || n.includes("limit") || n.includes("restricted")) return "regulated";
  if (typeHint === "line") return "coastline";
  return "generic";
}

function styleForRole(role, typeHint) {
  if (typeHint === "point") {
    if (role === "soundings") return { color: "#60a5fa", fillColor: "#60a5fa", radius: 1, weight: 0, fillOpacity: 0 };
    if (role === "lights") return { color: "#fbbf24", fillColor: "#fbbf24", radius: 4, weight: 1, fillOpacity: 0.9 };
    if (role === "regulated") return { color: "#a855f7", fillColor: "#a855f7", radius: 3, weight: 1, fillOpacity: 0.8 };
    return { color: "#93c5fd", fillColor: "#93c5fd", radius: 3, weight: 1, fillOpacity: 0.8 };
  }
  if (typeHint === "line") {
    if (role === "coastline") return { color: "#e5e7eb", weight: 1.4, opacity: 0.9 };
    if (role === "routes") return { color: "#22c55e", weight: 2, opacity: 0.95, dashArray: "6 4" };
    if (role === "regulated") return { color: "#a855f7", weight: 1.5, opacity: 0.9, dashArray: "3 3" };
    return { color: "#93c5fd", weight: 1.3, opacity: 0.9 };
  }
  if (role === "depths") return { color: "#38bdf8", weight: 1, opacity: 0.85, fillColor: "#020617", fillOpacity: 0.42 };
  if (role === "regulated") return { color: "#a855f7", weight: 1, opacity: 0.9, fillColor: "#4c1d95", fillOpacity: 0.24 };
  return { color: "#94a3b8", weight: 1, opacity: 0.85, fillColor: "#0f172a", fillOpacity: 0.22 };
}

function formatCoords(lat, lon) {
  return `${Number(lat).toFixed(5)} deg, ${Number(lon).toFixed(5)} deg`;
}

async function fetchJson(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function ringSignedArea(ring) {
  let s = 0;
  for (let i = 0; i < ring.length - 1; i += 1) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[i + 1];
    s += (x1 * y2) - (x2 * y1);
  }
  return s / 2;
}

function pointInRing(pt, ring) {
  const [x, y] = pt;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const intersect = ((yi > y) !== (yj > y))
      && (x < ((xj - xi) * (y - yi)) / ((yj - yi) || 1e-12) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

function esriGeometryToGeoJSON(geom) {
  if (!geom || typeof geom !== "object") return null;
  if (Number.isFinite(Number(geom.x)) && Number.isFinite(Number(geom.y))) {
    return { type: "Point", coordinates: [Number(geom.x), Number(geom.y)] };
  }
  if (Array.isArray(geom.points)) {
    return { type: "MultiPoint", coordinates: geom.points };
  }
  if (Array.isArray(geom.paths)) {
    if (geom.paths.length === 1) return { type: "LineString", coordinates: geom.paths[0] };
    return { type: "MultiLineString", coordinates: geom.paths };
  }
  if (Array.isArray(geom.rings) && geom.rings.length) {
    const outers = [];
    const holes = [];
    for (const ring of geom.rings) {
      if (!Array.isArray(ring) || ring.length < 4) continue;
      if (ringSignedArea(ring) < 0) outers.push([ring]);
      else holes.push(ring);
    }
    if (!outers.length) {
      return { type: "Polygon", coordinates: [geom.rings[0], ...geom.rings.slice(1)] };
    }
    for (const h of holes) {
      const h0 = h[0];
      const owner = outers.find((o) => pointInRing(h0, o[0]));
      if (owner) owner.push(h);
    }
    if (outers.length === 1) return { type: "Polygon", coordinates: outers[0] };
    return { type: "MultiPolygon", coordinates: outers };
  }
  return null;
}

function esriFeatureSetToGeoJSON(data) {
  const features = Array.isArray(data?.features) ? data.features : [];
  return {
    type: "FeatureCollection",
    features: features.map((f) => ({
      type: "Feature",
      geometry: esriGeometryToGeoJSON(f?.geometry),
      properties: f?.attributes && typeof f.attributes === "object" ? f.attributes : {},
    })).filter((f) => !!f.geometry),
  };
}

function deepClone(v) {
  try { return JSON.parse(JSON.stringify(v)); } catch { return null; }
}

function uid(prefix = "id") {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function downloadTextFile(filename, content, mime = "application/json") {
  const blob = new Blob([String(content || "")], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function downloadBlobFile(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = String(filename || "download.bin");
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function bboxFromGeometry(geometry) {
  if (!geometry || typeof geometry !== "object") return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  function visit(coords) {
    if (!Array.isArray(coords)) return;
    if (coords.length >= 2 && Number.isFinite(Number(coords[0])) && Number.isFinite(Number(coords[1]))) {
      const x = Number(coords[0]);
      const y = Number(coords[1]);
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
      return;
    }
    for (const c of coords) visit(c);
  }
  visit(geometry.coordinates);
  if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) return null;
  return [minX, minY, maxX, maxY];
}

function bboxIntersects(a, b) {
  if (!a || !b) return false;
  return !(a[2] < b[0] || a[0] > b[2] || a[3] < b[1] || a[1] > b[3]);
}

export const encChartsTool = {
  id: "enc-charts",
  name: "ENC Charts",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, containerEl) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(containerEl);
  },
  serialize() { return this._rt?.snapshot?.() ?? null; },
  restore(app, data) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.restore?.(data);
  },
};

function createRuntime(app) {
  const map = app.map.instance;
  function ensurePane(name, zIndex) {
    let p = map.getPane(name);
    if (!p) p = map.createPane(name);
    if (p && Number.isFinite(Number(zIndex))) p.style.zIndex = String(zIndex);
    return name;
  }
  ensurePane("enc-vg-base", 430);
  ensurePane("enc-vg-areas", 440);
  ensurePane("enc-vg-lines", 450);
  ensurePane("enc-vg-points", 460);
  ensurePane("enc-vg-labels", 470);

  let containerEl = null;
  let state = readState();
  let status = "ENC idle";
  let loading = false;
  let catalog = [];
  let moveTimer = null;
  let loadedCount = 0;
  let offMove = null;
  let removedRoot = false;
  const rootGroup = L.layerGroup().addTo(map);
  const layerRuntime = new Map();
  const layerLoadProgress = new Map();
  const depthBandsExpanded = new Set();
  const lightColorsExpanded = new Set();
  let zoomIndicatorEl = null;
  let exportAoiLayer = null;
  let exportDrawMode = false;
  let exportAoiDraft = [];
  let exportAoiPolyline = null;
  let exportAoiPolygon = null;
  let userChartsGroup = L.layerGroup().addTo(map);
  const userChartRuntime = new Map();
  let bottomMounted = false;
  let tableRows = [];
  let activeTableRowId = "";
  let userDrawMode = "";
  let userDrawDraft = [];
  let userDrawGuide = null;
  let userEditMode = false;
  let selectedUserFeatureRef = null;

  function paneForViewGroup(viewGroup, cfg) {
    const vg = String(viewGroup || "").toLowerCase();
    if (vg === "labels") return "enc-vg-labels";
    if (vg === "points") return "enc-vg-points";
    if (vg === "lines") return "enc-vg-lines";
    if (vg === "areas") return "enc-vg-areas";
    if (cfg?.typeHint === "point") return "enc-vg-points";
    if (cfg?.typeHint === "line") return "enc-vg-lines";
    if (cfg?.typeHint === "polygon") return "enc-vg-areas";
    return "enc-vg-base";
  }

  function shouldRenderSounding(_latlng, zoom) {
    const z = Number(zoom);
    const start = Math.max(6, Math.min(18, Number(state.soundingDotStartZoom || 15)));
    return Number.isFinite(z) && z >= start;
  }

  function shouldRenderSoundingDot(zoom) {
    const z = Number(zoom);
    const start = Math.max(6, Math.min(18, Number(state.soundingDotStartZoom || 15)));
    return Number.isFinite(z) && z >= start;
  }

  function updateZoomIndicator() {
    if (!zoomIndicatorEl) return;
    const z = Number(map.getZoom?.() ?? 0);
    zoomIndicatorEl.textContent = Number.isFinite(z) ? `Z${z.toFixed(1)}` : "Z?";
    zoomIndicatorEl.title = `Map zoom ${Number.isFinite(z) ? z.toFixed(1) : "?"} · sounding dots from z${Math.max(6, Math.min(18, Number(state.soundingDotStartZoom || 15)))}`;
  }

  function persist() {
    saveState(state);
  }

  function buildUserPresetData() {
    return {
      enabledLayerIds: [...(state.enabledLayerIds || [])],
      layerStyles: deepClone(state.layerStyles || {}) || {},
      depthBands: deepClone(state.depthBands || []) || [],
      lightColorMap: deepClone(state.lightColorMap || {}) || {},
      lightSymbologyMode: state.lightSymbologyMode,
      pointSymbologyMode: state.pointSymbologyMode,
      portrayalPackId: state.portrayalPackId,
      opacity: state.opacity,
      maxPerLayer: state.maxPerLayer,
      soundingDotStartZoom: state.soundingDotStartZoom,
    };
  }

  function applyUserPresetData(data) {
    if (!data || typeof data !== "object") return;
    state.enabledLayerIds = Array.isArray(data.enabledLayerIds) ? data.enabledLayerIds.map((x) => Number(x)).filter((x) => Number.isFinite(x)) : state.enabledLayerIds;
    state.layerStyles = data.layerStyles && typeof data.layerStyles === "object" ? data.layerStyles : state.layerStyles;
    state.depthBands = sanitizeDepthBands(data.depthBands || state.depthBands);
    state.lightColorMap = sanitizeLightColorMap(data.lightColorMap || state.lightColorMap);
    state.lightSymbologyMode = sanitizeLightSymbologyMode(data.lightSymbologyMode || state.lightSymbologyMode);
    state.pointSymbologyMode = sanitizePointSymbologyMode(data.pointSymbologyMode || state.pointSymbologyMode);
    state.portrayalPackId = sanitizePortrayalPackId(data.portrayalPackId || state.portrayalPackId);
    if (Number.isFinite(Number(data.opacity))) state.opacity = Math.max(0.05, Math.min(1, Number(data.opacity)));
    if (Number.isFinite(Number(data.maxPerLayer))) state.maxPerLayer = Math.max(200, Math.min(5000, Number(data.maxPerLayer)));
    if (Number.isFinite(Number(data.soundingDotStartZoom))) state.soundingDotStartZoom = Math.max(6, Math.min(18, Number(data.soundingDotStartZoom)));
    state.presetId = "none";
  }

  function saveUserPreset(nameRaw) {
    const name = String(nameRaw || "").trim();
    if (!name) return false;
    const next = Array.isArray(state.userPresets) ? [...state.userPresets] : [];
    const idx = next.findIndex((p) => String(p.name || "").toLowerCase() === name.toLowerCase());
    const entry = { name, data: buildUserPresetData(), updatedAt: new Date().toISOString() };
    if (idx >= 0) next[idx] = entry;
    else next.unshift(entry);
    state.userPresets = next.slice(0, 40);
    state.selectedUserPreset = name;
    persist();
    return true;
  }

  function loadUserPreset(nameRaw) {
    const name = String(nameRaw || "").trim();
    if (!name) return false;
    const p = (state.userPresets || []).find((x) => String(x?.name || "") === name);
    if (!p?.data) return false;
    applyUserPresetData(p.data);
    state.selectedUserPreset = name;
    persist();
    refreshEnabledLayers({ forceReload: true });
    render();
    return true;
  }

  function deleteUserPreset(nameRaw) {
    const name = String(nameRaw || "").trim();
    if (!name) return false;
    const before = Array.isArray(state.userPresets) ? state.userPresets.length : 0;
    state.userPresets = (state.userPresets || []).filter((p) => String(p?.name || "") !== name);
    if (state.selectedUserPreset === name) state.selectedUserPreset = "";
    persist();
    return (state.userPresets.length !== before);
  }

  function chartFeatureLabel(feature) {
    const p = feature?.properties || {};
    return String(p.objectClass || p.class || p.name || p.OBJNAM || p.__encLayerName || "Feature");
  }

  function geomTypeName(feature) {
    return String(feature?.geometry?.type || "Unknown");
  }

  function classTemplateKeys() {
    return Object.keys(getTemplateLibrary(state.templateLibraryId)?.classes || { S57_GENERIC: {} });
  }

  function templateForClass(objectClass) {
    const lib = getTemplateLibrary(state.templateLibraryId);
    const classes = lib?.classes || {};
    const k = String(objectClass || "").trim().toUpperCase();
    return classes[k] || classes.S57_GENERIC || { label: "Generic", geometry: ["Point", "LineString", "Polygon"], fields: [] };
  }

  function normalizeClassName(objectClass) {
    const lib = getTemplateLibrary(state.templateLibraryId);
    const classes = lib?.classes || {};
    const k = String(objectClass || "").trim().toUpperCase();
    return classes[k] ? k : "S57_GENERIC";
  }

  function applyTemplateDefaultsToFeature(feature, objectClassRaw = "") {
    if (!feature || typeof feature !== "object") return feature;
    const objectClass = normalizeClassName(objectClassRaw || feature?.properties?.objectClass || state.userChartDraftClass || "S57_GENERIC");
    const lib = getTemplateLibrary(state.templateLibraryId);
    const tpl = templateForClass(objectClass);
    const props = feature.properties && typeof feature.properties === "object" ? feature.properties : {};
    const next = { ...feature, properties: { ...props, objectClass } };
    for (const f of lib?.commonFields || []) {
      if (!Object.prototype.hasOwnProperty.call(next.properties, f.key) || next.properties[f.key] === undefined || next.properties[f.key] === null || next.properties[f.key] === "") {
        next.properties[f.key] = f.default ?? "";
      }
    }
    for (const f of tpl.fields || []) {
      if (!Object.prototype.hasOwnProperty.call(next.properties, f.key) || next.properties[f.key] === undefined || next.properties[f.key] === null || next.properties[f.key] === "") {
        next.properties[f.key] = f.default ?? "";
      }
    }
    return next;
  }

  function validateFeatureByTemplate(feature) {
    const props = feature?.properties && typeof feature.properties === "object" ? feature.properties : {};
    const objectClass = normalizeClassName(props.objectClass || "S57_GENERIC");
    const lib = getTemplateLibrary(state.templateLibraryId);
    const tpl = templateForClass(objectClass);
    const errors = [];
    const allFields = [...(lib?.commonFields || []), ...(tpl.fields || [])];
    const geomType = String(feature?.geometry?.type || "");
    if (Array.isArray(tpl.geometry) && tpl.geometry.length && !tpl.geometry.includes(geomType)) {
      errors.push(`Geometry ${geomType || "Unknown"} not allowed for ${objectClass}`);
    }
    for (const f of allFields) {
      const v = props[f.key];
      if (f.required && (v === undefined || v === null || String(v).trim() === "")) {
        errors.push(`${f.key} is required`);
        continue;
      }
      if (f.type === "number" && v !== undefined && v !== null && String(v).trim() !== "" && !Number.isFinite(Number(v))) {
        errors.push(`${f.key} must be numeric`);
      }
      if (f.type === "enum" && v !== undefined && v !== null && String(v).trim() !== "") {
        const n = Number(v);
        if (!Array.isArray(f.enum) || !f.enum.includes(n)) errors.push(`${f.key} must be one of [${(f.enum || []).join(", ")}]`);
      }
      if (f.type === "date_yyyymmdd" && v) {
        if (!/^\d{8}$/.test(String(v))) errors.push(`${f.key} must be YYYYMMDD`);
      }
      if (f.noSpacesAfterComma && v && String(v).includes(", ")) errors.push(`${f.key} should not contain spaces after commas`);
    }
    for (const r of tpl.rules || []) {
      if (r.kind === "atLeastOne" && Array.isArray(r.keys)) {
        const okAny = r.keys.some((k) => {
          const v = props[k];
          return !(v === undefined || v === null || String(v).trim() === "");
        });
        if (!okAny) errors.push(String(r.message || `One of ${r.keys.join(", ")} is required`));
      }
    }
    return { ok: errors.length < 1, objectClass, errors, template: tpl };
  }

  function getSelectedUserChartIndex() {
    return (state.userCharts || []).findIndex((c) => String(c.id) === String(state.selectedUserChartId));
  }

  function getSelectedUserChart() {
    const idx = getSelectedUserChartIndex();
    return idx >= 0 ? state.userCharts[idx] : null;
  }

  function ensureSelectedUserChart() {
    if (!Array.isArray(state.userCharts) || !state.userCharts.length) {
      state.selectedUserChartId = "";
      return null;
    }
    const idx = getSelectedUserChartIndex();
    if (idx >= 0) return state.userCharts[idx];
    state.selectedUserChartId = String(state.userCharts[0].id);
    return state.userCharts[0];
  }

  function addRowToTable(row) {
    const r = {
      id: String(row?.id || uid("row")),
      source: String(row?.source || "query"),
      chartId: String(row?.chartId || ""),
      featureId: String(row?.featureId || uid("feature")),
      objectClass: String(row?.objectClass || "S57_GENERIC"),
      geomType: String(row?.geomType || "Unknown"),
      name: String(row?.name || "Feature"),
      feature: deepClone(row?.feature) || null,
    };
    tableRows = [r, ...tableRows].slice(0, 800);
    activeTableRowId = r.id;
    renderBottomPanelOnly();
  }

  function queryFeatureToTable(cfg, feature, source = "enc-query") {
    if (!feature) return;
    const p = feature?.properties || {};
    const inferredClass = normalizeClassName(String(p.objectClass || inferRole(cfg?.name || cfg?.rawName || "", cfg?.typeHint || "") || "S57_GENERIC").toUpperCase());
    addRowToTable({
      source,
      featureId: String(feature.id || uid("f")),
      objectClass: inferredClass,
      geomType: geomTypeName(feature),
      name: String(p.OBJNAM || p.name || p.__encLayerName || cfg?.name || "Feature"),
      feature: applyTemplateDefaultsToFeature(feature, inferredClass),
    });
  }

  function onUserChartFeatureClick(chart, feature) {
    const p = feature?.properties || {};
    selectedUserFeatureRef = { chartId: String(chart?.id || ""), featureId: String(feature?.id || "") };
    addRowToTable({
      source: "user-chart",
      chartId: chart?.id,
      featureId: String(feature.id || uid("f")),
      objectClass: normalizeClassName(String(p.objectClass || p.class || "S57_GENERIC")),
      geomType: geomTypeName(feature),
      name: chartFeatureLabel(feature),
      feature: applyTemplateDefaultsToFeature(feature, String(p.objectClass || p.class || "S57_GENERIC")),
    });
  }

  function styleForUserChartFeature(feature) {
    const t = String(feature?.geometry?.type || "");
    const p = feature?.properties || {};
    const base = String(p.color || "#34d399");
    if (t.includes("Point")) return { radius: 5, color: "#111827", weight: 1.5, fillColor: base, fillOpacity: 0.95 };
    if (t.includes("Line")) return { color: base, weight: 2.2, opacity: 0.95, dashArray: p.dashArray || "" };
    return { color: base, weight: 1.5, opacity: 0.95, fillColor: base, fillOpacity: 0.18 };
  }

  function rebuildUserChartLayers() {
    if (!userChartsGroup) userChartsGroup = L.layerGroup().addTo(map);
    userChartsGroup.clearLayers();
    userChartRuntime.clear();
    for (const chart of state.userCharts || []) {
      if (chart?.visible === false) continue;
      const group = L.layerGroup().addTo(userChartsGroup);
      const features = Array.isArray(chart?.features) ? chart.features : [];
      const fc = { type: "FeatureCollection", features };
      const lyr = L.geoJSON(fc, {
        style: (f) => styleForUserChartFeature(f),
        pointToLayer: (f, latlng) => L.circleMarker(latlng, styleForUserChartFeature(f)),
        onEachFeature: (f, layer) => {
          const n = chartFeatureLabel(f);
          layer.bindPopup(`<div><strong>${esc(chart.name)}</strong><br/>${esc(n)}<br/><span class="small">${esc(geomTypeName(f))}</span></div>`);
          layer.on("click", () => onUserChartFeatureClick(chart, f));
        },
      });
      group.addLayer(lyr);
      userChartRuntime.set(String(chart.id), { chart, group, count: features.length });
    }
  }

  function createUserChart(nameRaw = "") {
    const name = String(nameRaw || "").trim() || `User Chart ${(state.userCharts || []).length + 1}`;
    const c = { id: uid("chart"), name, visible: true, features: [] };
    state.userCharts = [...(state.userCharts || []), c].slice(0, 60);
    state.selectedUserChartId = c.id;
    persist();
    rebuildUserChartLayers();
    render();
    setStatus(`Created user chart '${name}'.`);
  }

  function removeUserChart(chartId) {
    const id = String(chartId || "");
    if (!id) return;
    state.userCharts = (state.userCharts || []).filter((c) => String(c.id) !== id);
    ensureSelectedUserChart();
    persist();
    rebuildUserChartLayers();
    render();
    setStatus("User chart removed.");
  }

  function addFeatureToSelectedChart(featureInput, source = "manual") {
    const chart = ensureSelectedUserChart();
    if (!chart) {
      setStatus("Create/select a User Chart first.");
      return false;
    }
    const f = deepClone(featureInput);
    if (!f || !f.geometry) {
      setStatus("Invalid feature.");
      return false;
    }
    f.type = "Feature";
    f.id = String(f.id || uid("f"));
    f.properties = f.properties && typeof f.properties === "object" ? f.properties : {};
    f.properties.objectClass = normalizeClassName(f.properties.objectClass || state.userChartDraftClass || "S57_GENERIC");
    const fNorm = applyTemplateDefaultsToFeature(f, f.properties.objectClass);
    const validation = validateFeatureByTemplate(fNorm);
    if (!validation.ok) {
      setStatus(`Feature validation: ${validation.errors.join("; ")}`);
      return false;
    }
    f.properties._source = source;
    const idx = getSelectedUserChartIndex();
    if (idx < 0) return false;
    const next = deepClone(state.userCharts[idx]) || { ...chart, features: [] };
    next.features = [...(next.features || []), fNorm];
    state.userCharts = state.userCharts.map((c, i) => (i === idx ? next : c));
    persist();
    rebuildUserChartLayers();
    render();
    addRowToTable({
      source: "user-create",
      chartId: next.id,
      featureId: fNorm.id,
      objectClass: String(fNorm.properties.objectClass || "S57_GENERIC"),
      geomType: geomTypeName(fNorm),
      name: chartFeatureLabel(fNorm),
      feature: fNorm,
    });
    return true;
  }

  function addActiveTableRowToSelectedChart(rowId) {
    const row = tableRows.find((r) => String(r.id) === String(rowId));
    if (!row?.feature) return;
    addFeatureToSelectedChart(row.feature, row.source || "table");
    setStatus("Feature copied to selected User Chart.");
  }

  function findUserChartFeatureRefFromRow(row) {
    if (!row) return null;
    if (row.chartId && row.featureId) return { chartId: String(row.chartId), featureId: String(row.featureId) };
    return selectedUserFeatureRef ? { ...selectedUserFeatureRef } : null;
  }

  function updateUserChartFeatureByRef(ref, mutator) {
    if (!ref?.chartId || !ref?.featureId) return false;
    let changed = false;
    state.userCharts = (state.userCharts || []).map((c) => {
      if (String(c.id) !== String(ref.chartId)) return c;
      const features = (c.features || []).map((f) => {
        if (String(f.id) !== String(ref.featureId)) return f;
        const next = mutator(deepClone(f) || f);
        if (!next) return f;
        changed = true;
        return next;
      });
      return { ...c, features };
    });
    if (changed) {
      persist();
      rebuildUserChartLayers();
      render();
      renderBottomPanelOnly();
    }
    return changed;
  }

  function deleteSelectedTableFeature() {
    const row = tableRows.find((r) => String(r.id) === String(activeTableRowId));
    const ref = findUserChartFeatureRefFromRow(row);
    if (!ref) {
      setStatus("Select a User Chart feature row first.");
      return;
    }
    state.userCharts = (state.userCharts || []).map((c) => {
      if (String(c.id) !== String(ref.chartId)) return c;
      return { ...c, features: (c.features || []).filter((f) => String(f.id) !== String(ref.featureId)) };
    });
    tableRows = tableRows.filter((r) => !(String(r.chartId) === String(ref.chartId) && String(r.featureId) === String(ref.featureId)));
    activeTableRowId = "";
    selectedUserFeatureRef = null;
    persist();
    rebuildUserChartLayers();
    render();
    renderBottomPanelOnly();
    setStatus("Feature deleted from User Chart.");
  }

  function saveSelectedRowAttributesFromForm({ silent = false } = {}) {
    const row = tableRows.find((r) => String(r.id) === String(activeTableRowId));
    if (!row) return;
    const ref = findUserChartFeatureRefFromRow(row);
    if (!ref) {
      if (!silent) setStatus("Only User Chart features can be edited.");
      return;
    }
    const classInput = document.getElementById("enc-row-class");
    const className = normalizeClassName(classInput?.value || row.objectClass || "S57_GENERIC");
    const lib = getTemplateLibrary(state.templateLibraryId);
    const tpl = templateForClass(className);
    const allFields = [...(lib?.commonFields || []), ...(tpl.fields || [])];
    const nextFeature = deepClone(row.feature) || { type: "Feature", geometry: null, properties: {} };
    nextFeature.properties = nextFeature.properties && typeof nextFeature.properties === "object" ? nextFeature.properties : {};
    nextFeature.properties.objectClass = className;
    for (const field of allFields) {
      const el = document.getElementById(`enc-row-field-${field.key}`);
      if (!el) continue;
      const raw = String(el.value ?? "");
      if (field.type === "number" || field.type === "enum") nextFeature.properties[field.key] = raw === "" ? "" : Number(raw);
      else nextFeature.properties[field.key] = raw;
    }
    const finalFeature = applyTemplateDefaultsToFeature(nextFeature, className);
    const validation = validateFeatureByTemplate(finalFeature);
    if (!validation.ok) {
      if (!silent) setStatus(`Validation failed: ${validation.errors.join("; ")}`);
      return;
    }
    const ok = updateUserChartFeatureByRef(ref, () => finalFeature);
    if (!ok) {
      if (!silent) setStatus("Feature update failed.");
      return;
    }
    tableRows = tableRows.map((r) => {
      if (String(r.id) !== String(activeTableRowId)) return r;
      return {
        ...r,
        objectClass: className,
        geomType: geomTypeName(finalFeature),
        name: chartFeatureLabel(finalFeature),
        feature: deepClone(finalFeature),
      };
    });
    renderBottomPanelOnly();
    if (!silent) setStatus("Feature attributes updated.");
  }

  function moveSelectedFeatureVertexTo(latlng) {
    const row = tableRows.find((r) => String(r.id) === String(activeTableRowId));
    const ref = findUserChartFeatureRefFromRow(row);
    if (!ref || !latlng) return false;
    let moved = false;
    updateUserChartFeatureByRef(ref, (feature) => {
      const g = feature?.geometry;
      if (!g || !g.type) return feature;
      const t = String(g.type);
      const lng = Number(latlng.lng);
      const lat = Number(latlng.lat);
      if (t === "Point") {
        g.coordinates = [lng, lat];
        moved = true;
        return feature;
      }
      if (t === "LineString" && Array.isArray(g.coordinates) && g.coordinates.length) {
        let bestIdx = 0;
        let bestD = Infinity;
        for (let i = 0; i < g.coordinates.length; i += 1) {
          const c = g.coordinates[i];
          const d = Math.hypot(Number(c?.[0] || 0) - lng, Number(c?.[1] || 0) - lat);
          if (d < bestD) { bestD = d; bestIdx = i; }
        }
        g.coordinates[bestIdx] = [lng, lat];
        moved = true;
        return feature;
      }
      if (t === "Polygon" && Array.isArray(g.coordinates?.[0]) && g.coordinates[0].length >= 4) {
        const ring = g.coordinates[0];
        let bestIdx = 0;
        let bestD = Infinity;
        for (let i = 0; i < ring.length - 1; i += 1) {
          const c = ring[i];
          const d = Math.hypot(Number(c?.[0] || 0) - lng, Number(c?.[1] || 0) - lat);
          if (d < bestD) { bestD = d; bestIdx = i; }
        }
        ring[bestIdx] = [lng, lat];
        ring[ring.length - 1] = [...ring[0]];
        moved = true;
        return feature;
      }
      return feature;
    });
    if (moved) {
      const chart = getSelectedUserChart();
      const f = (chart?.features || []).find((x) => String(x.id) === String(ref.featureId));
      if (f) {
        tableRows = tableRows.map((r) => (String(r.id) === String(activeTableRowId) ? { ...r, feature: deepClone(f), geomType: geomTypeName(f), name: chartFeatureLabel(f) } : r));
      }
      renderBottomPanelOnly();
      setStatus("Moved nearest vertex to clicked location.");
    }
    return moved;
  }

  function clearTableRows() {
    tableRows = [];
    activeTableRowId = "";
    renderBottomPanelOnly();
  }

  function stopUserDrawMode() {
    userDrawMode = "";
    userDrawDraft = [];
    if (userDrawGuide) { try { map.removeLayer(userDrawGuide); } catch {} }
    userDrawGuide = null;
    if (!exportDrawMode) {
      try { map.doubleClickZoom.enable(); } catch {}
      try { map.getContainer().style.cursor = ""; } catch {}
    }
    renderBottomPanelOnly();
  }

  function updateUserDrawGuide() {
    if (!userDrawMode) return;
    if (userDrawGuide) { try { map.removeLayer(userDrawGuide); } catch {} }
    userDrawGuide = null;
    if (userDrawDraft.length < 1) return;
    if (userDrawMode === "line" || userDrawMode === "polygon") {
      userDrawGuide = L.polyline(userDrawDraft, { color: "#22d3ee", weight: 2, dashArray: "6 4", opacity: 0.95 }).addTo(map);
      if (userDrawMode === "polygon" && userDrawDraft.length >= 3) {
        const poly = L.polygon(userDrawDraft, { color: "#22d3ee", weight: 1.5, fillOpacity: 0.1, opacity: 0.95 });
        poly.addTo(map);
        userDrawGuide = poly;
      }
    }
  }

  function startUserDrawMode(mode) {
    const m = String(mode || "");
    if (!["point", "line", "polygon"].includes(m)) return;
    const chart = ensureSelectedUserChart();
    if (!chart) {
      setStatus("Create/select a User Chart first.");
      return;
    }
    stopExportDrawMode();
    userDrawMode = m;
    userDrawDraft = [];
    if (userDrawGuide) { try { map.removeLayer(userDrawGuide); } catch {} }
    userDrawGuide = null;
    try { map.doubleClickZoom.disable(); } catch {}
    try { map.getContainer().style.cursor = "crosshair"; } catch {}
    setStatus(`Create ${m}: click map ${m === "point" ? "once" : "vertices, double-click to finish"}.`);
    renderBottomPanelOnly();
  }

  function completeUserDraw() {
    if (!userDrawMode) return;
    if (userDrawMode === "line" && userDrawDraft.length >= 2) {
      addFeatureToSelectedChart({
        type: "Feature",
        geometry: { type: "LineString", coordinates: userDrawDraft.map((ll) => [ll.lng, ll.lat]) },
        properties: { objectClass: state.userChartDraftClass || "S57_GENERIC" },
      }, "draw");
    } else if (userDrawMode === "polygon" && userDrawDraft.length >= 3) {
      const ring = userDrawDraft.map((ll) => [ll.lng, ll.lat]);
      const first = ring[0];
      const last = ring[ring.length - 1];
      if (!last || last[0] !== first[0] || last[1] !== first[1]) ring.push([first[0], first[1]]);
      addFeatureToSelectedChart({
        type: "Feature",
        geometry: { type: "Polygon", coordinates: [ring] },
        properties: { objectClass: state.userChartDraftClass || "S57_GENERIC" },
      }, "draw");
    } else {
      setStatus("Not enough vertices to complete feature.");
    }
    stopUserDrawMode();
  }

  function renderUserCharts() {
    const selectedId = String(state.selectedUserChartId || "");
    const charts = state.userCharts || [];
    if (!charts.length) return `<div class=\"small\">No User Charts yet.</div>`;
    return charts.map((c) => {
      const cid = String(c.id);
      const count = Array.isArray(c.features) ? c.features.length : 0;
      return `
        <div class=\"toolset-row\" style=\"padding:4px 2px; display:flex; align-items:center; justify-content:space-between; gap:8px; border:none; background:transparent; border-bottom:1px solid rgba(148,163,184,.18);\">
          <label style=\"display:flex; align-items:center; gap:6px; min-width:0; cursor:pointer;\">
            <input type=\"radio\" name=\"enc-user-chart-select\" data-enc-user-chart-select=\"${esc(cid)}\" ${selectedId === cid ? "checked" : ""} />
            <span class=\"toolset-name\" style=\"white-space:nowrap; overflow:hidden; text-overflow:ellipsis;\">${esc(c.name)}</span>
          </label>
          <div style=\"display:flex; align-items:center; gap:6px;\">
            <label class=\"small\" style=\"display:flex; align-items:center; gap:4px;\"><input type=\"checkbox\" data-enc-user-chart-visible=\"${esc(cid)}\" ${c.visible !== false ? "checked" : ""} /> on</label>
            <span class=\"small\" style=\"padding:1px 6px;border-radius:999px;border:1px solid rgba(148,163,184,.45);\">${count}</span>
            <button class=\"btn\" data-enc-user-chart-remove=\"${esc(cid)}\" style=\"padding:2px 8px;\">X</button>
          </div>
        </div>
      `;
    }).join("");
  }

  function renderFeatureTableBody() {
    const chart = getSelectedUserChart();
    const rows = tableRows.slice(0, 400);
    const modeLabel = userDrawMode ? `Drawing ${userDrawMode}` : "Idle";
    const activeRow = tableRows.find((r) => String(r.id) === String(activeTableRowId)) || null;
    const activeFeature = activeRow?.feature || null;
    const activeClass = normalizeClassName(activeRow?.objectClass || activeFeature?.properties?.objectClass || state.userChartDraftClass || "S57_GENERIC");
    const templateLib = getTemplateLibrary(state.templateLibraryId);
    const activeTemplate = templateForClass(activeClass);
    const draftClass = normalizeClassName(state.userChartDraftClass || "S57_GENERIC");
    const draftClassOptions = classTemplateKeys().map((k) => {
      const lbl = templateForClass(k)?.label || k;
      return `<option value="${esc(k)}"${k === draftClass ? " selected" : ""}>${esc(k)} - ${esc(lbl)}</option>`;
    }).join("");
    const classOptions = classTemplateKeys().map((k) => {
      const lbl = templateForClass(k)?.label || k;
      return `<option value="${esc(k)}"${k === activeClass ? " selected" : ""}>${esc(k)} - ${esc(lbl)}</option>`;
    }).join("");
    const editorFields = [...(templateLib?.commonFields || []), ...(activeTemplate.fields || [])];
    const fieldEditors = editorFields.map((f) => {
      const pv = activeFeature?.properties?.[f.key];
      const val = pv === undefined || pv === null ? "" : String(pv);
      const valueControl = f.key === "objectClass"
        ? `<select id="enc-row-class" style="width:100%;" ${activeRow ? "" : "disabled"}>${classOptions}</select>`
        : `<input id="enc-row-field-${esc(f.key)}" data-enc-row-cell-key="${esc(f.key)}" type="${f.type === "number" ? "number" : "text"}" value="${esc(val)}" style="width:100%;" ${activeRow ? "" : "disabled"} />`;
      return `
        <tr>
          <td style="padding:4px 6px; border-bottom:1px solid rgba(148,163,184,.14); white-space:nowrap;">${esc(f.key)}${f.required ? "*" : ""}</td>
          <td style="padding:4px 6px; border-bottom:1px solid rgba(148,163,184,.14);">${valueControl}</td>
        </tr>
      `;
    }).join("");
    const editHint = userEditMode ? "Edit Mode: click map to move selected point/nearest vertex." : "Edit Mode off";
    const dockOptions = `
      <option value="bottom"${state.featureTableDock === "bottom" ? " selected" : ""}>Bottom Pane</option>
      <option value="panel"${state.featureTableDock === "panel" ? " selected" : ""}>ENC Panel</option>
    `;
    return `
      <div class="row" style="margin-top:6px; gap:8px; flex-wrap:wrap; align-items:center;">
        <span class="small">Chart:</span>
        <span class="small" style="font-weight:700;">${esc(chart?.name || "None selected")}</span>
        <span class="small" style="opacity:.8;">${esc(modeLabel)}</span>
        <label class="small" style="margin-left:8px;">Dock</label>
        <select id="enc-feature-table-dock" style="width:130px;">${dockOptions}</select>
        <label class="small" style="margin-left:8px;">Create Class</label>
        <select id="enc-user-draft-class" style="width:220px;">${draftClassOptions}</select>
        <button id="enc-draw-point" class="btn">Point</button>
        <button id="enc-draw-line" class="btn">Line</button>
        <button id="enc-draw-polygon" class="btn">Area</button>
        <button id="enc-draw-finish" class="btn" ${userDrawMode ? "" : "disabled"}>Finish</button>
        <button id="enc-draw-cancel" class="btn" ${userDrawMode ? "" : "disabled"}>Cancel</button>
        <button id="enc-toggle-edit-mode" class="btn">${userEditMode ? "Edit On" : "Edit Off"}</button>
        <button id="enc-delete-feature" class="btn" ${activeRow ? "" : "disabled"}>Delete Feature</button>
        <button id="enc-table-clear" class="btn">Clear Table</button>
      </div>
      <div class="small" style="margin-top:6px; opacity:.88;">Click ENC/user-chart features to query into table. Use row Add to copy queried features into selected chart. ${esc(editHint)}</div>
      <div style="margin-top:6px; padding:6px; border:1px solid rgba(148,163,184,.2); border-radius:8px; background:rgba(15,23,42,.4);">
        <div class="small" style="font-weight:700; margin-bottom:4px;">Attribute Editor</div>
        <div class="small" style="opacity:.85; margin-bottom:4px;">Library: ${esc(templateLib?.label || "Unknown")} (auto-save on cell edit)</div>
        <div style="max-height:180px; overflow:auto;">
          <table style="width:100%; border-collapse:collapse; font-size:12px;">
            <thead>
              <tr>
                <th style="text-align:left; padding:4px 6px; border-bottom:1px solid rgba(148,163,184,.2); width:180px;">Attribute</th>
                <th style="text-align:left; padding:4px 6px; border-bottom:1px solid rgba(148,163,184,.2);">Value</th>
              </tr>
            </thead>
            <tbody>
              ${fieldEditors || `<tr><td colspan="2" class="small" style="padding:6px;">Select a feature row to edit attributes.</td></tr>`}
            </tbody>
          </table>
        </div>
      </div>
      <div style="margin-top:6px; flex:1; min-height:0; overflow:auto;">
        <table style="width:100%; border-collapse:collapse; font-size:12px;">
          <thead>
            <tr>
              <th style="text-align:left; padding:6px; border-bottom:1px solid rgba(148,163,184,.2);">Source</th>
              <th style="text-align:left; padding:6px; border-bottom:1px solid rgba(148,163,184,.2);">Class</th>
              <th style="text-align:left; padding:6px; border-bottom:1px solid rgba(148,163,184,.2);">Geom</th>
              <th style="text-align:left; padding:6px; border-bottom:1px solid rgba(148,163,184,.2);">Name</th>
              <th style="text-align:right; padding:6px; border-bottom:1px solid rgba(148,163,184,.2);">Action</th>
            </tr>
          </thead>
          <tbody>
            ${rows.length ? rows.map((r) => `
              <tr data-enc-table-row="${esc(r.id)}" style="border-bottom:1px solid rgba(148,163,184,.12); background:${activeTableRowId === r.id ? "rgba(56,189,248,.12)" : "transparent"};">
                <td style="padding:6px;">${esc(r.source)}</td>
                <td style="padding:6px;">${esc(r.objectClass)}</td>
                <td style="padding:6px;">${esc(r.geomType)}</td>
                <td style="padding:6px; max-width:420px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${esc(r.name)}</td>
                <td style="padding:6px; text-align:right;"><button class="btn" data-enc-row-add="${esc(r.id)}" style="padding:2px 8px;">Add</button></td>
              </tr>
            `).join("") : `<tr><td colspan="5" style="padding:8px;" class="small">No queried features yet.</td></tr>`}
          </tbody>
        </table>
      </div>
    `;
  }

  function renderBottomPanelHtml() {
    return `
      <div class="card" id="enc-bottom-card" style="height:100%; display:flex; flex-direction:column;">
        <details id="enc-feature-table-root-bottom" class="toolset-group" ${state.featureTableOpen ? "open" : ""} style="height:100%; display:flex; flex-direction:column;">
          <summary>ENC Feature Table</summary>
          <div class="toolset-content" style="height:100%; display:flex; flex-direction:column; min-height:0;">
            ${renderFeatureTableBody()}
          </div>
        </details>
      </div>
    `;
  }

  function bindFeatureTableControls(host) {
    if (!host) return;
    const onClickBottom = (ev) => {
      const row = ev.target?.closest?.("[data-enc-table-row]");
      if (row) {
        activeTableRowId = String(row.getAttribute("data-enc-table-row") || "");
        const rr = tableRows.find((r) => String(r.id) === activeTableRowId);
        if (rr?.chartId && rr?.featureId) selectedUserFeatureRef = { chartId: String(rr.chartId), featureId: String(rr.featureId) };
        renderBottomPanelOnly();
        return;
      }
      const addBtn = ev.target?.closest?.("[data-enc-row-add]");
      if (addBtn) {
        addActiveTableRowToSelectedChart(String(addBtn.getAttribute("data-enc-row-add") || ""));
        return;
      }
      if (ev.target?.closest?.("#enc-table-clear")) { clearTableRows(); return; }
      if (ev.target?.closest?.("#enc-draw-point")) { startUserDrawMode("point"); return; }
      if (ev.target?.closest?.("#enc-draw-line")) { startUserDrawMode("line"); return; }
      if (ev.target?.closest?.("#enc-draw-polygon")) { startUserDrawMode("polygon"); return; }
      if (ev.target?.closest?.("#enc-draw-finish")) { completeUserDraw(); return; }
      if (ev.target?.closest?.("#enc-draw-cancel")) { stopUserDrawMode(); return; }
      if (ev.target?.closest?.("#enc-toggle-edit-mode")) {
        userEditMode = !userEditMode;
        if (userEditMode) stopUserDrawMode();
        renderBottomPanelOnly();
        return;
      }
      if (ev.target?.closest?.("#enc-delete-feature")) { deleteSelectedTableFeature(); return; }
    };
    const onInputBottom = (ev) => {
      if (ev.target?.id === "enc-feature-table-dock") {
        const nextDock = String(ev.target.value || "bottom") === "panel" ? "panel" : "bottom";
        if (state.featureTableDock !== nextDock) {
          state.featureTableDock = nextDock;
          persist();
          syncFeatureTableDock();
          render();
          renderBottomPanelOnly();
        }
        return;
      }
      if (ev.target?.id === "enc-user-draft-class") {
        state.userChartDraftClass = normalizeClassName(String(ev.target.value || "S57_GENERIC"));
        persist();
        return;
      }
      if (ev.target?.id === "enc-row-class") {
        const row = tableRows.find((r) => String(r.id) === String(activeTableRowId));
        if (!row) return;
        row.objectClass = normalizeClassName(String(ev.target.value || row.objectClass || "S57_GENERIC"));
        if (row.feature?.properties) row.feature.properties.objectClass = row.objectClass;
        renderBottomPanelOnly();
      }
    };
    const onChangeBottom = (ev) => {
      const t = ev?.target;
      if (!t) return;
      if (t.id === "enc-row-class" || t.dataset?.encRowCellKey) {
        saveSelectedRowAttributesFromForm({ silent: true });
      }
    };
    host.onclick = onClickBottom;
    host.oninput = onInputBottom;
    host.onchange = onChangeBottom;
    host.ontoggle = (ev) => {
      const t = ev?.target;
      if (!t?.id) return;
      if (t.id === "enc-feature-table-root-bottom" || t.id === "enc-feature-table-root-inline") {
        state.featureTableOpen = !!t.open;
        persist();
      }
    };
  }

  function bindBottomPanelControls() {
    bindFeatureTableControls(document.getElementById("enc-bottom-card"));
  }

  function bindInlineFeatureTableControls() {
    const host = document.getElementById("enc-inline-feature-table");
    bindFeatureTableControls(host);
  }

  function syncFeatureTableDock() {
    if (state.featureTableDock === "bottom") {
      ensureBottomPanel();
      return;
    }
    if (bottomMounted) {
      app.ui.unmountBottomPanel("enc-charts");
      bottomMounted = false;
    }
  }

  function renderBottomPanelOnly() {
    if (state.featureTableDock !== "bottom") {
      if (bottomMounted) {
        app.ui.unmountBottomPanel("enc-charts");
        bottomMounted = false;
      }
      bindInlineFeatureTableControls();
      return;
    }
    if (!bottomMounted) return;
    app.ui.mountBottomPanel(renderBottomPanelHtml(), {
      ownerId: "enc-charts",
      title: "ENC Charts",
      onShow: () => {
        bindBottomPanelControls();
      },
    });
  }

  function ensureBottomPanel() {
    if (state.featureTableDock !== "bottom") return;
    if (bottomMounted) return;
    app.ui.mountBottomPanel(renderBottomPanelHtml(), {
      ownerId: "enc-charts",
      title: "ENC Charts",
      onShow: () => {
        bindBottomPanelControls();
      },
    });
    bottomMounted = true;
  }

  function ensureExportLayer() {
    if (exportAoiLayer) return;
    exportAoiLayer = L.layerGroup().addTo(map);
  }

  function aoiRingFromBounds(bounds) {
    return [
      [bounds.getWest(), bounds.getSouth()],
      [bounds.getEast(), bounds.getSouth()],
      [bounds.getEast(), bounds.getNorth()],
      [bounds.getWest(), bounds.getNorth()],
      [bounds.getWest(), bounds.getSouth()],
    ];
  }

  function clearExportAoiDrawing() {
    exportAoiDraft = [];
    if (exportAoiPolyline) { try { exportAoiLayer?.removeLayer(exportAoiPolyline); } catch {} }
    if (exportAoiPolygon) { try { exportAoiLayer?.removeLayer(exportAoiPolygon); } catch {} }
    exportAoiPolyline = null;
    exportAoiPolygon = null;
  }

  function stopExportDrawMode() {
    exportDrawMode = false;
    try { map.doubleClickZoom.enable(); } catch {}
    try { map.getContainer().style.cursor = ""; } catch {}
    render();
  }

  function startExportDrawMode() {
    stopUserDrawMode();
    ensureExportLayer();
    clearExportAoiDrawing();
    exportDrawMode = true;
    state.exportOpen = true;
    try { map.doubleClickZoom.disable(); } catch {}
    try { map.getContainer().style.cursor = "crosshair"; } catch {}
    persist();
    setStatus("Draw polygon: click to add vertices, double-click to finish.");
    render();
  }

  function updateExportDraftGraphics() {
    ensureExportLayer();
    if (exportAoiPolyline) { try { exportAoiLayer.removeLayer(exportAoiPolyline); } catch {} }
    if (exportAoiPolygon) { try { exportAoiLayer.removeLayer(exportAoiPolygon); } catch {} }
    exportAoiPolyline = null;
    exportAoiPolygon = null;

    if (exportAoiDraft.length >= 2) {
      exportAoiPolyline = L.polyline(exportAoiDraft, { color: "#22d3ee", weight: 2, dashArray: "5 4", opacity: 0.9 }).addTo(exportAoiLayer);
    }
    if (exportAoiDraft.length >= 3) {
      exportAoiPolygon = L.polygon(exportAoiDraft, { color: "#22d3ee", weight: 2, opacity: 0.95, fillOpacity: 0.08 }).addTo(exportAoiLayer);
    }
  }

  function currentAoiRingLonLat() {
    if (state.exportMode === "bounds") {
      return aoiRingFromBounds(map.getBounds());
    }
    if (exportAoiDraft.length >= 3) {
      const ring = exportAoiDraft.map((ll) => [Number(ll.lng), Number(ll.lat)]);
      const f = ring[0];
      const l = ring[ring.length - 1];
      if (!f || !l || f[0] !== l[0] || f[1] !== l[1]) ring.push([f[0], f[1]]);
      return ring;
    }
    return null;
  }

  function buildExportFeatureCollection() {
    const ring = currentAoiRingLonLat();
    if (!ring) {
      setStatus("Export requires a polygon AOI when mode is User polygon.");
      return null;
    }
    const aoiPoly = {
      type: "Feature",
      properties: { mode: state.exportMode },
      geometry: { type: "Polygon", coordinates: [ring] },
    };
    const aoiBbox = bboxFromGeometry(aoiPoly.geometry);
    const enabled = getEnabledSet();
    const outFeatures = [];
    for (const [serviceId, rt] of layerRuntime.entries()) {
      if (!enabled.has(Number(serviceId))) continue;
      const cfg = rt?.cfg || catalog.find((c) => c.serviceId === Number(serviceId));
      const feats = Array.isArray(rt?.data?.features) ? rt.data.features : [];
      for (const f of feats) {
        const fb = bboxFromGeometry(f?.geometry);
        if (!bboxIntersects(fb, aoiBbox)) continue;
        const copy = deepClone(f);
        if (!copy) continue;
        copy.properties = {
          ...(copy.properties || {}),
          __encLayerId: Number(serviceId),
          __encLayerName: cfg?.name || `Layer ${serviceId}`,
        };
        outFeatures.push(copy);
      }
    }
    return {
      type: "FeatureCollection",
      features: outFeatures,
      metadata: {
        exportedAt: new Date().toISOString(),
        exportMode: state.exportMode,
        aoi: aoiPoly.geometry,
      },
    };
  }

  function exportCurrentFeatures() {
    const fc = buildExportFeatureCollection();
    if (!fc) return;
    const ts = new Date().toISOString().replace(/[:.]/g, "-");
    downloadTextFile(`enc_export_${state.exportMode}_${ts}.geojson`, JSON.stringify(fc, null, 2), "application/geo+json");
    setStatus(`Exported ${fc.features.length.toLocaleString()} feature(s).`);
  }

  async function exportCurrentS57() {
    const fc = buildExportFeatureCollection();
    if (!fc) return;
    const endpoint = String(state.s57ExportUrl || "").trim();
    if (!endpoint) {
      setStatus("S-57 export error: missing converter URL.");
      return;
    }
    const cellName = String(state.s57CellName || "").trim() || "US5TEST";
    const edition = Math.max(1, Math.min(999, Number(state.s57Edition || 1)));
    const updateNumber = Math.max(0, Math.min(9999, Number(state.s57UpdateNumber || 0)));
    setStatus(`Exporting S-57 (.000) from ${fc.features.length.toLocaleString()} feature(s)...`);
    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          featureCollection: fc,
          metadata: { cellName, edition, updateNumber, gmCompat: true },
        }),
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          if (j?.error) msg = j.error;
        } catch {}
        throw new Error(msg);
      }
      const blob = await res.blob();
      const cd = String(res.headers.get("content-disposition") || "");
      const m = cd.match(/filename\*?=(?:UTF-8'')?\"?([^\";]+)\"?/i);
      const fallbackName = `${cellName || "enc_export"}.000`;
      const filename = m?.[1] ? decodeURIComponent(m[1]) : fallbackName;
      downloadBlobFile(filename, blob);
      setStatus(`S-57 export ready (${fc.features.length.toLocaleString()} feature(s)).`);
    } catch (err) {
      setStatus(`S-57 export error: ${err?.message || String(err)}`);
    }
  }

  function getEnabledSet() {
    return new Set((state.enabledLayerIds || []).map((x) => Number(x)));
  }

  function getLayerStyleOverride(serviceId) {
    const key = String(serviceId);
    const raw = state.layerStyles?.[key];
    if (!raw || typeof raw !== "object") return { color: "", weight: "", lineType: "solid" };
    const weight = Number(raw.weight);
    return {
      color: typeof raw.color === "string" ? raw.color : "",
      weight: Number.isFinite(weight) ? Math.max(0.3, Math.min(8, weight)) : "",
      lineType: raw.lineType && LINE_TYPE_TO_DASH[raw.lineType] !== undefined ? raw.lineType : "solid",
    };
  }

  function setLayerStyleOverride(serviceId, patch) {
    const key = String(serviceId);
    const next = { ...getLayerStyleOverride(serviceId), ...(patch || {}) };
    state.layerStyles = { ...(state.layerStyles || {}), [key]: next };
    persist();
  }

  function removeLayerStyleOverride(serviceId) {
    const key = String(serviceId);
    if (!state.layerStyles || !Object.prototype.hasOwnProperty.call(state.layerStyles, key)) return;
    const next = { ...state.layerStyles };
    delete next[key];
    state.layerStyles = next;
    persist();
  }

  function styleForConfig(cfg) {
    const base = { ...styleForRole(cfg.role, cfg.typeHint) };
    const ov = getLayerStyleOverride(cfg.serviceId);

    if (ov.color) {
      if (cfg.typeHint === "point") {
        base.color = ov.color;
        base.fillColor = ov.color;
      } else if (cfg.typeHint === "line") {
        base.color = ov.color;
      } else if (cfg.typeHint === "polygon") {
        base.color = ov.color;
        base.fillColor = ov.color;
      }
    }

    if ((cfg.typeHint === "line" || cfg.typeHint === "polygon") && Number.isFinite(Number(ov.weight))) {
      base.weight = Number(ov.weight);
    }
    if ((cfg.typeHint === "line" || cfg.typeHint === "polygon") && ov.lineType && LINE_TYPE_TO_DASH[ov.lineType] !== undefined) {
      base.dashArray = LINE_TYPE_TO_DASH[ov.lineType] || null;
    }
    return base;
  }

  function getDepthValue(props) {
    const p = props || {};
    const candidates = [p.DRVAL1, p.DRVAL2, p.VALSOU, p.Z, p.DEPTH];
    for (const v of candidates) {
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
    return null;
  }

  function bandForDepth(depth) {
    const bands = sanitizeDepthBands(state.depthBands);
    for (const b of bands) {
      if (b.max === null) return b;
      if (depth <= b.max) return b;
    }
    return bands[bands.length - 1];
  }

  function styleForFeature(cfg, feature) {
    return evaluatePortrayal(cfg, feature).style;
  }

  function getLightPrimaryCode(props) {
    const colorRaw = String(props?.COLOUR || "").trim();
    return colorRaw.split(",").map((x) => x.trim()).find((x) => /^\d+$/.test(x)) || "";
  }

  function colorForLightCode(code) {
    const lightMap = sanitizeLightColorMap(state.lightColorMap);
    const c = lightMap[String(code || "").trim()];
    return c || null;
  }

  function colorForAnyCode(code, fallback = "#93c5fd") {
    const c = String(code || "").trim();
    if (c === "1") return "#f8fafc";
    if (c === "2") return "#111827";
    if (c === "3") return "#ef4444";
    if (c === "4") return "#22c55e";
    if (c === "5") return "#3b82f6";
    if (c === "6") return "#fbbf24";
    return fallback;
  }

  function normalizeCatlit(v) {
    return String(v || "").toLowerCase().trim();
  }

  function lightIconShape(catlit, fallbackShape = "circle") {
    const c = normalizeCatlit(catlit);
    if (!c) return fallbackShape;
    if (c.includes("leading") || c.includes("front") || c.includes("rear")) return "triangle";
    if (c.includes("air obstruction") || c.includes("aero")) return "diamond";
    return "circle";
  }

  function lightIconSize(catlit) {
    const c = normalizeCatlit(catlit);
    if (c.includes("leading") || c.includes("front") || c.includes("rear")) return 16;
    if (c.includes("air obstruction") || c.includes("aero")) return 14;
    return 12;
  }

  function lightIconHtml(props, opts = {}) {
    const code = getLightPrimaryCode(props);
    const fill = colorForLightCode(code) || "#fbbf24";
    const shape = lightIconShape(props?.CATLIT, opts.fallbackShape || "circle");
    const size = lightIconSize(props?.CATLIT);
    const s = size;
    if (shape === "triangle") {
      return `<svg width="${s}" height="${s}" viewBox="0 0 24 24" aria-hidden="true">
        <polygon points="12,3 22,21 2,21" fill="${esc(fill)}" stroke="#111827" stroke-width="2"/>
      </svg>`;
    }
    if (shape === "diamond") {
      return `<svg width="${s}" height="${s}" viewBox="0 0 24 24" aria-hidden="true">
        <polygon points="12,2 22,12 12,22 2,12" fill="${esc(fill)}" stroke="#111827" stroke-width="2"/>
      </svg>`;
    }
    return `<svg width="${s}" height="${s}" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="8" fill="${esc(fill)}" stroke="#111827" stroke-width="2"/>
    </svg>`;
  }

  function pointSymbolType(cfg) {
    const n = String(cfg?.rawName || cfg?.name || "").toLowerCase();
    if (n.includes("buoy")) return "buoy";
    if (n.includes("beacon") || n.includes("daymark") || n.includes("topmark")) return "beacon";
    if (n.includes("wreck")) return "wreck";
    if (n.includes("obstruction") || n.includes("rock")) return "hazard";
    if (n.includes("light")) return "light";
    return "generic";
  }

  function pointSymbolHtml(symbol, color) {
    const fill = esc(color || "#93c5fd");
    if (symbol === "beacon") {
      return `<svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
        <rect x="7" y="10" width="10" height="10" fill="${fill}" stroke="#111827" stroke-width="2"/>
        <polygon points="12,4 18,10 6,10" fill="${fill}" stroke="#111827" stroke-width="2"/>
      </svg>`;
    }
    if (symbol === "buoy") {
      return `<svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
        <line x1="12" y1="3" x2="12" y2="8" stroke="#111827" stroke-width="2"/>
        <circle cx="12" cy="14" r="6" fill="${fill}" stroke="#111827" stroke-width="2"/>
      </svg>`;
    }
    if (symbol === "wreck") {
      return `<svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
        <line x1="5" y1="5" x2="19" y2="19" stroke="${fill}" stroke-width="3"/>
        <line x1="19" y1="5" x2="5" y2="19" stroke="${fill}" stroke-width="3"/>
      </svg>`;
    }
    if (symbol === "hazard") {
      return `<svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
        <polygon points="12,4 21,20 3,20" fill="${fill}" stroke="#111827" stroke-width="2"/>
      </svg>`;
    }
    if (symbol === "light") return lightIconHtml({ COLOUR: "6", CATLIT: "" });
    return `<svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="7" fill="${fill}" stroke="#111827" stroke-width="2"/>
    </svg>`;
  }

  const portrayalEngine = createPortrayalEngine({
    getState: () => state,
    helpers: {
      styleForConfig,
      getDepthValue,
      bandForDepth,
      getLightPrimaryCode,
      colorForLightCode,
      colorForAnyCode,
      lightIconSize,
      lightIconHtml,
      pointSymbolType,
      pointSymbolHtml,
    },
  });

  function evaluatePortrayal(cfg, feature) {
    return portrayalEngine.evaluatePortrayal(cfg, feature);
  }

  function setStatus(s) {
    status = String(s || "");
    render();
  }

  function applyPreset(presetId) {
    const p = ENC_LAYER_PRESETS.find((x) => x.id === presetId) || ENC_LAYER_PRESETS[0];
    state.presetId = p.id;
    state.enabledLayerIds = [...p.serviceIds];
    persist();
    refreshEnabledLayers({ forceReload: true });
    render();
  }

  async function loadCatalog() {
    loading = true;
    setStatus("Loading ENC service metadata...");
    try {
      const serviceMeta = await fetchJson(`${ENC_VECTOR_SERVICE_BASE}?f=pjson`);
      const layers = Array.isArray(serviceMeta?.layers) ? serviceMeta.layers : [];
      const overrideById = new Map(ENC_LAYER_OVERRIDES.map((x) => [Number(x.serviceId), x]));
      catalog = layers
        .filter((lyr) => Number.isFinite(Number(lyr.id)) && String(lyr.type || "").toLowerCase() === "feature layer")
        .map((lyr) => {
          const serviceId = Number(lyr.id);
          const ov = overrideById.get(serviceId) || {};
          const rawName = String(lyr.name || lyr.label || "");
          const geometryType = String(lyr.geometryType || "");
          const typeHint = inferTypeHint(geometryType, rawName);
          if (typeHint === "unknown") return null;
          const role = ov.s52Role || inferRole(lyr.name || lyr.label, typeHint);
          return {
            serviceId,
            group: lyr.parentLayerId >= 0 ? `Group ${lyr.parentLayerId}` : "ENC Layers",
            name: String(ov.label || rawName || `Layer ${serviceId}`),
            rawName,
            geometryType,
            role,
            typeHint,
            minZoom: Number.isFinite(Number(ov.minZoom)) ? Number(ov.minZoom) : 7,
            defaultVisible: !!ov.defaultVisible,
            depthField: ov.depthField || "Z",
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name));

      if (!state.enabledLayerIds.length) {
        const navPreset = ENC_LAYER_PRESETS.find((p) => p.id === "nav_safety");
        state.enabledLayerIds = navPreset ? [...navPreset.serviceIds] : catalog.slice(0, 4).map((c) => c.serviceId);
        state.presetId = navPreset ? navPreset.id : "none";
      }
      loadedCount = 0;
      persist();
      setStatus(`ENC catalog loaded (${catalog.length} layers).`);
      refreshEnabledLayers({ forceReload: true });
    } catch (err) {
      console.error("[ENC] catalog load failed", err);
      setStatus(`ENC catalog error: ${err?.message || String(err)}`);
    } finally {
      loading = false;
      render();
    }
  }

  async function ensureLayerQueryMeta(cfg) {
    if (cfg._queryMetaReady) return;
    try {
      const m = await fetchJson(`${ENC_VECTOR_SERVICE_BASE}/${cfg.serviceId}?f=pjson`);
      cfg.objectIdField = typeof m?.objectIdField === "string" ? m.objectIdField : "OBJECTID";
      cfg.serverMaxRecordCount = Number.isFinite(Number(m?.maxRecordCount)) ? Number(m.maxRecordCount) : 1000;
    } catch {
      cfg.objectIdField = cfg.objectIdField || "OBJECTID";
      cfg.serverMaxRecordCount = cfg.serverMaxRecordCount || 1000;
    }
    cfg._queryMetaReady = true;
  }

  function clearLayer(serviceId) {
    const rt = layerRuntime.get(serviceId);
    if (!rt) return;
    try { rt.group.clearLayers(); } catch {}
    try { rootGroup.removeLayer(rt.group); } catch {}
    layerRuntime.delete(serviceId);
    layerLoadProgress.delete(Number(serviceId));
  }

  function renderActiveLayersOnly() {
    const el = containerEl?.querySelector?.("#enc-active-layers-list");
    if (el) el.innerHTML = renderActiveLayers();
  }

  function bindPopup(feature, layer, cfg) {
    const p = feature?.properties || {};
    const keys = Object.keys(p).slice(0, 12);
    const rows = keys.map((k) => `<div><strong>${esc(k)}:</strong> ${esc(p[k])}</div>`).join("");
    const latlng = layer.getLatLng?.();
    const coord = latlng ? `<div><strong>Coord:</strong> ${esc(formatCoords(latlng.lat, latlng.lng))}</div>` : "";
    layer.bindPopup(`
      <div style=\"min-width:240px;\"> 
        <div><strong>${esc(cfg.name)}</strong></div>
        ${coord}
        <hr style=\"border:0;border-top:1px solid rgba(148,163,184,.25);margin:6px 0;\" />
        ${rows || "<div>No attributes</div>"}
      </div>
    `);
  }

  function geoJsonLayerForConfig(cfg, geojson) {
    const firstFeature = Array.isArray(geojson?.features) && geojson.features.length ? geojson.features[0] : null;
    const firstPortrayal = evaluatePortrayal(cfg, firstFeature);
    const layerPane = paneForViewGroup(firstPortrayal?.viewGroup, cfg);
    return L.geoJSON(geojson, {
      pane: layerPane,
      style: (feature) => ({ ...styleForFeature(cfg, feature) }),
      pointToLayer: (feature, latlng) => {
        const portrayal = evaluatePortrayal(cfg, feature);
        const featureStyle = portrayal.style || styleForConfig(cfg);
        const pointPane = paneForViewGroup(portrayal?.viewGroup, cfg);
        if (portrayal.pointRenderer?.kind === "label") {
          if (!shouldRenderSounding(latlng, map.getZoom())) {
            if (shouldRenderSoundingDot(map.getZoom())) {
              return L.circleMarker(latlng, {
                radius: 1.8,
                color: "#0b1220",
                weight: 1,
                fillColor: "#f8fafc",
                fillOpacity: 0.9,
                opacity: 0.95,
                pane: pointPane,
                interactive: false,
              });
            }
            return L.circleMarker(latlng, {
              radius: 0,
              opacity: 0,
              fillOpacity: 0,
              interactive: false,
              pane: pointPane,
            });
          }
          const haloColor = String(portrayal.pointRenderer.haloColor || "rgba(255,255,255,0.62)");
          const haloWidth = Number.isFinite(Number(portrayal.pointRenderer.haloWidthPx)) ? Number(portrayal.pointRenderer.haloWidthPx) : 1;
          const textColor = String(portrayal.pointRenderer.color || featureStyle.color || "#000");
          const blurPx = Math.max(0.6, haloWidth * 1.1);
          const shadow = `0 0 ${blurPx}px ${haloColor}`;
          return L.marker(latlng, {
            icon: L.divIcon({
              className: "enc-sounding-label",
              html: `<span style=\"font-size:10px;color:${esc(textColor)};text-shadow:${esc(shadow)};\">${esc(portrayal.pointRenderer.text || "")}</span>`,
              iconSize: [28, 12],
              iconAnchor: [14, 6],
            }),
            pane: pointPane,
            interactive: true,
          });
        }
        if (portrayal.pointRenderer?.kind === "icon") {
          const size = portrayal.pointRenderer.size || [16, 16];
          const anchor = portrayal.pointRenderer.anchor || [8, 8];
          return L.marker(latlng, {
            icon: L.divIcon({
              className: portrayal.pointRenderer.className || "enc-point-icon",
              html: portrayal.pointRenderer.html || "",
              iconSize: size,
              iconAnchor: anchor,
            }),
            pane: pointPane,
            interactive: true,
          });
        }
        return L.circleMarker(latlng, { ...featureStyle, pane: pointPane });
      },
      onEachFeature: (feature, layer) => {
        bindPopup(feature, layer, cfg);
        layer.on("click", () => queryFeatureToTable(cfg, feature, "enc-query"));
      },
    });
  }

  async function loadOneLayer(cfg, { onProgress } = {}) {
    const z = map.getZoom();
    if (z < cfg.minZoom) {
      clearLayer(cfg.serviceId);
      return;
    }
    const b = map.getBounds();
    const geometryEnvelope = JSON.stringify({
      xmin: b.getWest(),
      ymin: b.getSouth(),
      xmax: b.getEast(),
      ymax: b.getNorth(),
      spatialReference: { wkid: 4326 },
    });
    await ensureLayerQueryMeta(cfg);
    const requestedMax = Math.max(1, Number(state.maxPerLayer || 1500));
    const serverPageMax = Math.max(1, Number(cfg.serverMaxRecordCount || 1000));
    const pageSize = Math.min(serverPageMax, requestedMax);
    const allEsriFeatures = [];
    let resultOffset = 0;
    let keepPaging = true;

    while (keepPaging && allEsriFeatures.length < requestedMax) {
      const remaining = requestedMax - allEsriFeatures.length;
      const batchSize = Math.min(pageSize, remaining);
      const query = new URLSearchParams({
        where: "1=1",
        outFields: "*",
        f: "json",
        returnGeometry: "true",
        outSR: "4326",
        inSR: "4326",
        geometryType: "esriGeometryEnvelope",
        geometry: geometryEnvelope,
        spatialRel: "esriSpatialRelIntersects",
        resultRecordCount: String(batchSize),
        resultOffset: String(resultOffset),
      });
      if (cfg.objectIdField) query.set("orderByFields", `${cfg.objectIdField} ASC`);

      const url = `${ENC_VECTOR_SERVICE_BASE}/${cfg.serviceId}/query?${query.toString()}`;
      // eslint-disable-next-line no-await-in-loop
      let page = await fetchJson(url);
      if (page?.error && cfg.objectIdField) {
        query.delete("orderByFields");
        const retryUrl = `${ENC_VECTOR_SERVICE_BASE}/${cfg.serviceId}/query?${query.toString()}`;
        // eslint-disable-next-line no-await-in-loop
        page = await fetchJson(retryUrl);
      }

      const pageFeatures = Array.isArray(page?.features) ? page.features : [];
      allEsriFeatures.push(...pageFeatures);
      resultOffset += pageFeatures.length;

      const hitTransferLimit = !!page?.exceededTransferLimit;
      keepPaging = hitTransferLimit && pageFeatures.length > 0 && resultOffset < requestedMax;
      if (pageFeatures.length < batchSize) keepPaging = false;
      onProgress?.({
        phase: keepPaging ? "loading" : "done",
        loaded: allEsriFeatures.length,
        limit: requestedMax,
      });
    }

    const data = esriFeatureSetToGeoJSON({ features: allEsriFeatures });

    let rt = layerRuntime.get(cfg.serviceId);
    if (!rt) {
      rt = { cfg, group: L.layerGroup(), count: 0 };
      layerRuntime.set(cfg.serviceId, rt);
      rootGroup.addLayer(rt.group);
    }
    rt.group.clearLayers();

    const lyr = geoJsonLayerForConfig(cfg, data);
    lyr.setStyle?.({ opacity: state.opacity, fillOpacity: Math.min(0.7, state.opacity * 0.55) });
    rt.group.addLayer(lyr);

    const count = Array.isArray(data.features) ? data.features.length : 0;
    rt.count = count;
    rt.data = data;
    loadedCount += count;
    onProgress?.({
      phase: "done",
      loaded: count,
      limit: requestedMax,
    });
  }

  async function refreshEnabledLayers({ forceReload = false } = {}) {
    if (removedRoot) {
      rootGroup.addTo(map);
      removedRoot = false;
    }

    const enabled = getEnabledSet();
    const allIds = catalog.map((c) => c.serviceId);

    for (const id of allIds) {
      if (!enabled.has(id)) clearLayer(id);
    }

    if (!catalog.length) {
      render();
      return;
    }

    const tasks = catalog.filter((c) => enabled.has(c.serviceId));
    if (!tasks.length) {
      setStatus("ENC ready (no enabled layers).");
      return;
    }

    loading = true;
    setStatus(`Loading ENC layers (${tasks.length})...`);
    loadedCount = 0;
    for (const cfg of tasks) {
      layerLoadProgress.set(cfg.serviceId, {
        phase: "loading",
        loaded: 0,
        limit: Number(state.maxPerLayer || 1500),
      });
    }
    renderActiveLayersOnly();

    for (const cfg of tasks) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await loadOneLayer(cfg, {
          onProgress: (p) => {
            layerLoadProgress.set(cfg.serviceId, {
              phase: String(p?.phase || "loading"),
              loaded: Number(p?.loaded || 0),
              limit: Math.max(1, Number(p?.limit || state.maxPerLayer || 1500)),
            });
            renderActiveLayersOnly();
          },
        });
      } catch (err) {
        console.warn("[ENC] layer load failed", cfg.serviceId, err);
        layerLoadProgress.set(cfg.serviceId, {
          phase: "error",
          loaded: 0,
          limit: Math.max(1, Number(state.maxPerLayer || 1500)),
        });
        renderActiveLayersOnly();
      }
    }

    loading = false;
    setStatus(loadedCount > 0
      ? `ENC ready (${loadedCount.toLocaleString()} features).`
      : "ENC ready (0 features in current view/zoom).");
    if (forceReload) render();
  }

  function debounceRefresh() {
    if (!state.autoRefresh) return;
    if (moveTimer) clearTimeout(moveTimer);
    moveTimer = setTimeout(() => {
      refreshEnabledLayers({ forceReload: false });
    }, ENC_VECTOR_DEBOUNCE_MS);
  }

  function updateFromForm() {
    if (!containerEl) return;
    state.opacity = Math.max(0.05, Math.min(1, Number(containerEl.querySelector("#enc-opacity")?.value || 0.9)));
    state.autoRefresh = !!containerEl.querySelector("#enc-auto-refresh")?.checked;
    state.maxPerLayer = Math.max(200, Math.min(5000, Number(containerEl.querySelector("#enc-max-per")?.value || 1500)));
    state.presetId = String(containerEl.querySelector("#enc-preset")?.value || "none");
    state.pointSymbologyMode = sanitizePointSymbologyMode(containerEl.querySelector("#enc-point-symbology")?.value || state.pointSymbologyMode);
    state.portrayalPackId = sanitizePortrayalPackId(containerEl.querySelector("#enc-portrayal-pack")?.value || state.portrayalPackId);
    state.templateLibraryId = sanitizeTemplateLibraryId(containerEl.querySelector("#enc-template-library")?.value || state.templateLibraryId);
    state.portrayalCatalogUrl = String(containerEl.querySelector("#enc-portrayal-url")?.value || state.portrayalCatalogUrl || "");
    state.soundingDotStartZoom = Math.max(6, Math.min(18, Number(containerEl.querySelector("#enc-sounding-dot-zoom")?.value || state.soundingDotStartZoom || 15)));
    state.layerFilterText = String(containerEl.querySelector("#enc-layer-filter")?.value || "");
    state.s57ExportUrl = String(containerEl.querySelector("#enc-s57-export-url")?.value || state.s57ExportUrl || "").trim();
    state.s57CellName = String(containerEl.querySelector("#enc-s57-cell-name")?.value || state.s57CellName || "US5TST01").trim().slice(0, 8);
    state.s57Edition = Math.max(1, Math.min(999, Number(containerEl.querySelector("#enc-s57-edition")?.value || state.s57Edition || 1)));
    state.s57UpdateNumber = Math.max(0, Math.min(9999, Number(containerEl.querySelector("#enc-s57-update-number")?.value || state.s57UpdateNumber || 0)));
    persist();
  }

  function depthBandLabel(bands, idx) {
    const b = bands[idx];
    const prev = idx > 0 ? bands[idx - 1] : null;
    if (!b) return "";
    if (b.max === null) return `>${prev?.max ?? 0} m`;
    if (!prev) return `0-${b.max} m`;
    return `${prev.max}-${b.max} m`;
  }

  function renderLightColorsEditor(layerId, compact = false) {
    const map = sanitizeLightColorMap(state.lightColorMap);
    const mode = sanitizeLightSymbologyMode(state.lightSymbologyMode);
    const rows = Object.keys(LIGHT_COLOR_LABELS).map((code) => `
      <div style=\"display:grid; grid-template-columns: 1fr 24px; align-items:center; gap:6px; padding:${compact ? "2px 0" : "4px 0"};\">
        <span class=\"small\">${esc(LIGHT_COLOR_LABELS[code])}</span>
        <input type=\"color\" value=\"${esc(map[code] || DEFAULT_LIGHT_COLOR_MAP[code] || "#f8fafc")}\" data-enc-light-color-code=\"${code}\" data-enc-light-layer=\"${layerId}\" title=\"Light color ${esc(code)}\" style=\"width:24px;height:24px;padding:0;border:none;background:transparent;\" />
      </div>
    `).join("");
    return `
      <div style=\"padding:2px 6px 4px 6px;\">
        <div style=\"display:grid; grid-template-columns: 1fr 110px; align-items:center; gap:6px; padding:${compact ? "2px 0" : "4px 0"};\">
          <span class=\"small\">Symbology</span>
          <select data-enc-light-mode=\"1\" style=\"width:110px;\">
            <option value=\"simple\"${mode === LIGHT_SYMBOLOGY_MODES.simple ? " selected" : ""}>Simple Dot</option>
            <option value=\"enc_icon\"${mode === LIGHT_SYMBOLOGY_MODES.enc_icon ? " selected" : ""}>ENC Icon</option>
          </select>
        </div>
        ${rows}
      </div>
    `;
  }

  function renderDepthBandsRows(layerId, compact = false) {
    const bands = sanitizeDepthBands(state.depthBands);
    const rows = bands.map((b, idx) => {
      const maxInput = b.max === null
        ? `<span class=\"small\" style=\"opacity:.85;\">inf</span>`
        : `<input type=\"number\" step=\"0.1\" min=\"0\" value=\"${esc(b.max)}\" data-enc-depth-band-max=\"${idx}\" style=\"width:62px;\" title=\"Band upper depth (m)\" />`;
      return `
        <div style=\"display:grid; grid-template-columns: 1fr 62px 24px; align-items:center; gap:6px; padding:${compact ? "2px 0" : "4px 0"};\">
          <span class=\"small\">${esc(depthBandLabel(bands, idx))}</span>
          ${maxInput}
          <input type=\"color\" value=\"${esc(b.color)}\" data-enc-depth-band-color=\"${idx}\" data-enc-depth-layer=\"${layerId}\" title=\"Band color\" style=\"width:24px;height:24px;padding:0;border:none;background:transparent;\" />
        </div>
      `;
    }).join("");
    return `<div style=\"padding:2px 6px 4px 6px;\">${rows}</div>`;
  }

  function renderLayerChecklist() {
    if (!catalog.length) return `<div class=\"small\">Load ENC catalog to list layers.</div>`;
    const enabled = getEnabledSet();
    const filter = String(state.layerFilterText || "").trim().toLowerCase();
    const groups = [
      { title: "Polygons", typeHint: "polygon" },
      { title: "Points", typeHint: "point" },
      { title: "Lines", typeHint: "line" },
    ];

    function renderRow(c) {
      const isDepthLayer = c.role === "depths" && c.typeHint === "polygon";
      const isLightLayer = c.role === "lights" && c.typeHint === "point";
      const isExpanded = depthBandsExpanded.has(c.serviceId);
      const isLightExpanded = lightColorsExpanded.has(c.serviceId);
      const depthToggle = isDepthLayer
        ? `<span style=\"display:inline-block; width:12px; text-align:center;\">${isExpanded ? "?" : "?"}</span>`
        : "";
      const lightToggle = isLightLayer
        ? `<span style=\"display:inline-block; width:12px; text-align:center;\">${isLightExpanded ? "?" : "?"}</span>`
        : "";
      const expandableKind = isDepthLayer ? "depth" : (isLightLayer ? "light" : "");
      const toggleGlyph = isDepthLayer ? depthToggle : lightToggle;
      const leftName = expandableKind
        ? `<button type=\"button\" data-enc-layer-expand=\"${c.serviceId}\" data-enc-expand-kind=\"${expandableKind}\" style=\"display:flex;align-items:center;gap:6px;border:0;background:transparent;padding:0;margin:0;cursor:pointer;color:inherit;\">
            ${toggleGlyph}
            <span class=\"toolset-name\" style=\"white-space:nowrap; overflow:hidden; text-overflow:ellipsis;\">${esc(c.name)}</span>
          </button>`
        : `<span class=\"toolset-name\" style=\"white-space:nowrap; overflow:hidden; text-overflow:ellipsis;\">${esc(c.name)}</span>`;
      return `
        <div class=\"toolset-row\" style=\"padding:4px 2px; display:flex; align-items:center; justify-content:space-between; gap:8px; border:none; border-radius:0; background:transparent; border-bottom:1px solid rgba(148,163,184,.18);\">
          <span style=\"display:flex; align-items:center; gap:6px; min-width:0;\">${leftName}</span>
          <span><input type=\"checkbox\" data-enc-layer-toggle=\"${c.serviceId}\" ${enabled.has(c.serviceId) ? "checked" : ""} /></span>
        </div>
        ${isDepthLayer && isExpanded ? renderDepthBandsRows(c.serviceId, true) : ""}
        ${isLightLayer && isLightExpanded ? renderLightColorsEditor(c.serviceId, true) : ""}
      `;
    }

    return groups.map((g) => {
      const rows = catalog
        .filter((c) => inferTypeHint(c.geometryType, c.rawName || c.name) === g.typeHint)
        .filter((c) => !filter || String(c.name || "").toLowerCase().includes(filter));
      const enabledCount = rows.filter((c) => enabled.has(c.serviceId)).length;
      return `
        <details class=\"toolset-group\" open style=\"margin-bottom:8px;\">
          <summary>${esc(g.title)} (${enabledCount}/${rows.length})</summary>
          <div class=\"toolset-content\">
            ${rows.length ? rows.map(renderRow).join("") : `<div class=\"small\">No ${esc(g.title.toLowerCase())} layers found.</div>`}
          </div>
        </details>
      `;
    }).join("");
  }

  function renderActiveLayers() {
    const enabled = getEnabledSet();
    const active = catalog.filter((c) => enabled.has(c.serviceId)).sort((a, b) => a.name.localeCompare(b.name));
    if (!active.length) return `<div class=\"small\">No active layers. Check a layer below to activate it.</div>`;

    return active.map((c) => {
      const ov = getLayerStyleOverride(c.serviceId);
      const effective = styleForConfig(c);
      const count = layerRuntime.get(c.serviceId)?.count;
      const countText = Number.isFinite(count) ? count.toLocaleString() : "—";
      const lp = layerLoadProgress.get(c.serviceId);
      const lpPhase = String(lp?.phase || "idle");
      const lpLoaded = Number(lp?.loaded || 0);
      const lpLimit = Math.max(1, Number(lp?.limit || state.maxPerLayer || 1500));
      const lpPct = lpPhase === "done" ? 100 : Math.max(0, Math.min(96, Math.round((lpLoaded / lpLimit) * 100)));
      const lpColor = lpPhase === "error"
        ? "#ef4444"
        : lpPhase === "done"
          ? "#22c55e"
          : "#60a5fa";
      const loadLine = `
        <span title=\"Load status\" style=\"display:inline-flex; align-items:center; width:42px;\">
          <span style=\"display:block; width:42px; height:2px; background:rgba(148,163,184,.28); border-radius:999px;\">
            <span style=\"display:block; width:${lpPct}%; height:2px; background:${lpColor}; border-radius:999px; transition:width .18s linear;\"></span>
          </span>
        </span>
      `;
      const colorValue = ov.color || effective.color || effective.fillColor || "#60a5fa";
      const weightValue = Number.isFinite(Number(ov.weight)) ? Number(ov.weight) : (Number.isFinite(Number(effective.weight)) ? Number(effective.weight) : 1.4);
      const lineTypeValue = ov.lineType || inferLineTypeFromDash(effective.dashArray);
      const hasStrokeControls = c.typeHint === "line" || c.typeHint === "polygon";
      const isDepthLayer = c.role === "depths" && c.typeHint === "polygon";
      const isLightLayer = c.role === "lights" && c.typeHint === "point";
      const isExpanded = depthBandsExpanded.has(c.serviceId);
      const isLightExpanded = lightColorsExpanded.has(c.serviceId);
      const depthToggle = isDepthLayer
        ? `<span style=\"display:inline-block; width:12px; text-align:center;\">${isExpanded ? "?" : "?"}</span>`
        : "";
      const lightToggle = isLightLayer
        ? `<span style=\"display:inline-block; width:12px; text-align:center;\">${isLightExpanded ? "?" : "?"}</span>`
        : `<span style=\"display:inline-block; width:16px;\"></span>`;
      const expandableKind = isDepthLayer ? "depth" : (isLightLayer ? "light" : "");
      const toggleGlyph = isDepthLayer ? depthToggle : lightToggle;
      const leftName = expandableKind
        ? `<button type=\"button\" data-enc-layer-expand=\"${c.serviceId}\" data-enc-expand-kind=\"${expandableKind}\" style=\"display:flex;align-items:center;gap:6px;border:0;background:transparent;padding:0;margin:0;cursor:pointer;color:inherit;\">
            ${toggleGlyph}
            <div class=\"toolset-name\" style=\"min-width:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;\">${esc(c.name)}</div>
          </button>`
        : `<div class=\"toolset-name\" style=\"min-width:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;\">${esc(c.name)}</div>`;
      const lineTypeSelect = hasStrokeControls
        ? `<select data-enc-active-line-type=\"${c.serviceId}\" title=\"Line type\" style=\"width:86px; font-family:monospace;\">
            <option value=\"solid\"${lineTypeValue === "solid" ? " selected" : ""}>------</option>
            <option value=\"dashed\"${lineTypeValue === "dashed" ? " selected" : ""}>- - - -</option>
            <option value=\"dotted\"${lineTypeValue === "dotted" ? " selected" : ""}>······</option>
          </select>`
        : `<span style=\"display:inline-block; width:86px;\"></span>`;
      const weightInput = hasStrokeControls
        ? `<input type=\"number\" min=\"0.3\" max=\"8\" step=\"0.1\" value=\"${esc(weightValue)}\" data-enc-active-weight=\"${c.serviceId}\" style=\"width:62px;\" title=\"Line weight\" />`
        : `<span style=\"display:inline-block; width:62px;\"></span>`;

      const rowCore = `
        <div class=\"toolset-row\" style=\"padding:4px 2px; display:flex; align-items:center; justify-content:space-between; gap:8px; border:none; border-radius:0; background:transparent; border-bottom:1px solid rgba(148,163,184,.18);\">
          <div style=\"display:flex; align-items:center; gap:6px; min-width:0;\">
            ${leftName}
          </div>
          <div style=\"display:grid; grid-template-columns: 86px 62px 26px 42px 58px 34px; align-items:center; justify-content:end; column-gap:6px; flex:0 0 auto;\">
            ${lineTypeSelect}
            ${weightInput}
            <input type=\"color\" value=\"${esc(colorValue)}\" data-enc-active-color=\"${c.serviceId}\" title=\"Layer color\" style=\"width:24px;height:24px;padding:0;border:none;background:transparent;\" />
            ${loadLine}
            <span class=\"small\" title=\"Features currently in map view\" style=\"padding:1px 6px;border-radius:999px;border:1px solid rgba(148,163,184,.45);\">${countText}</span>
            <button class=\"btn\" data-enc-active-remove=\"${c.serviceId}\" title=\"Remove from active layers\" style=\"padding:2px 8px;\">X</button>
          </div>
        </div>
      `;
      return `
        ${rowCore}
        ${isDepthLayer && isExpanded ? renderDepthBandsRows(c.serviceId, true) : ""}
        ${isLightLayer && isLightExpanded ? renderLightColorsEditor(c.serviceId, true) : ""}
      `;
    }).join("");
  }

  function render() {
    if (!containerEl) return;
    const pct = Math.round(state.opacity * 100);
    const presetOptions = ENC_LAYER_PRESETS.map((p) => `<option value=\"${esc(p.id)}\"${p.id === state.presetId ? " selected" : ""}>${esc(p.label)}</option>`).join("");
    const userPresetOptions = [`<option value=\"\">Select saved preset...</option>`]
      .concat((state.userPresets || []).map((p) => `<option value=\"${esc(p.name)}\"${p.name === state.selectedUserPreset ? " selected" : ""}>${esc(p.name)}</option>`))
      .join("");
    const portrayalOptions = Object.values(PORTRAYAL_PACKS)
      .map((p) => `<option value=\"${esc(p.id)}\"${p.id === state.portrayalPackId ? " selected" : ""}>${esc(p.label)}</option>`)
      .join("");
    const templateLibOptions = listTemplateLibraryOptions()
      .map((x) => `<option value=\"${esc(x.id)}\"${x.id === sanitizeTemplateLibraryId(state.templateLibraryId) ? " selected" : ""}>${esc(x.label)}</option>`)
      .join("");
    const selectedPortrayalPreset = PORTRAYAL_XML_PRESETS.find((p) => p.url === state.portrayalCatalogUrl)?.url || "__custom";
    const portrayalXmlPresetOptions = [
      `<option value="__custom"${selectedPortrayalPreset === "__custom" ? " selected" : ""}>Custom URL</option>`,
      ...PORTRAYAL_XML_PRESETS.map((p) => `<option value="${esc(p.url)}"${selectedPortrayalPreset === p.url ? " selected" : ""}>${esc(p.label)}</option>`),
    ].join("");

    containerEl.innerHTML = `
      <div class=\"card\" style=\"padding:10px;\">
        <details id=\"enc-controls-root\" class=\"toolset-group\" ${state.controlsOpen ? "open" : ""}>
          <summary>ENC Controls</summary>
          <div class=\"toolset-content\">
            <div class=\"small\" style=\"margin-top:6px;\">
              NOAA ENC Direct vector layers via ArcGIS query -> GeoJSON -> Leaflet rendering.
            </div>

            <div class=\"row lm-add-row\" style=\"margin-top:8px;\">
              <button id=\"enc-load-catalog\" class=\"btn primary\" ${loading ? "disabled" : ""}>Load Catalog</button>
              <button id=\"enc-refresh\" class=\"btn\" ${loading ? "disabled" : ""}>Refresh Visible</button>
              <button id=\"enc-clear\" class=\"btn\">Clear</button>
            </div>

            <div class=\"row lm-add-row\" style=\"margin-top:8px; align-items:center;\">
              <label class=\"small\" style=\"min-width:60px;\">Preset</label>
              <select id=\"enc-preset\" style=\"flex:1;\">${presetOptions}</select>
              <button id=\"enc-apply-preset\" class=\"btn\">Apply</button>
            </div>

            <div class=\"row lm-add-row\" style=\"margin-top:6px; align-items:center;\">
              <input id=\"enc-user-preset-name\" type=\"text\" placeholder=\"Preset name\" style=\"flex:1;\" />
              <button id=\"enc-save-user-preset\" class=\"btn\">Save</button>
            </div>
            <div class=\"row lm-add-row\" style=\"margin-top:6px; align-items:center;\">
              <select id=\"enc-user-preset-select\" style=\"flex:1;\">${userPresetOptions}</select>
              <button id=\"enc-load-user-preset\" class=\"btn\">Load</button>
              <button id=\"enc-delete-user-preset\" class=\"btn\">Del</button>
            </div>

            <div class=\"row lm-add-row\" style=\"margin-top:8px; align-items:center;\">
              <label class=\"small\" style=\"min-width:100px;\">Point Symbols</label>
              <select id=\"enc-point-symbology\" style=\"flex:1;\">
                <option value=\"dot\"${state.pointSymbologyMode === POINT_SYMBOLOGY_MODES.dot ? " selected" : ""}>Dot</option>
                <option value=\"nautical\"${state.pointSymbologyMode === POINT_SYMBOLOGY_MODES.nautical ? " selected" : ""}>Nautical Icons</option>
              </select>
            </div>

            <div class=\"row lm-add-row\" style=\"margin-top:6px; align-items:center;\">
              <label class=\"small\" style=\"min-width:100px;\">Portrayal</label>
              <select id=\"enc-portrayal-pack\" style=\"flex:1;\">${portrayalOptions}</select>
            </div>

            <div class=\"row lm-add-row\" style=\"margin-top:6px; align-items:center;\">
              <label class=\"small\" style=\"min-width:100px;\">Templates</label>
              <select id=\"enc-template-library\" style=\"flex:1;\">${templateLibOptions}</select>
            </div>

            <details class=\"toolset-group\" style=\"margin-top:6px;\">
              <summary>Portrayal Source</summary>
              <div class=\"toolset-content\" style=\"padding-top:6px;\">
                <div class=\"row lm-add-row\" style=\"align-items:center; margin-bottom:6px;\">
                  <select id=\"enc-portrayal-url-preset\" style=\"flex:1;\">${portrayalXmlPresetOptions}</select>
                </div>
                <div class=\"row lm-add-row\" style=\"align-items:center;\">
                  <input id=\"enc-portrayal-url\" type=\"text\" value=\"${esc(state.portrayalCatalogUrl || "")}\" placeholder=\"Portrayal XML URL\" style=\"flex:1;\" />
                  <button id=\"enc-load-portrayal-xml\" class=\"btn\">Load XML</button>
                </div>
                <div class=\"small\" style=\"margin-top:4px; opacity:.85;\">Loader stub: parses XML manifest and registers a runtime portrayal pack.</div>
              </div>
            </details>

            <div class=\"row lm-add-row\" style=\"margin-top:8px; align-items:center;\">
              <label class=\"small\" style=\"min-width:60px;\">Opacity</label>
              <input id=\"enc-opacity\" type=\"range\" min=\"0.05\" max=\"1\" step=\"0.01\" value=\"${state.opacity}\" style=\"flex:1;\" />
              <span class=\"small\">${pct}%</span>
            </div>

            <div class=\"row lm-add-row\" style=\"margin-top:6px; align-items:center;\">
              <label class=\"small\" style=\"min-width:120px;\">Sounding dots from z</label>
              <input id=\"enc-sounding-dot-zoom\" type=\"number\" min=\"6\" max=\"18\" step=\"0.5\" value=\"${esc(state.soundingDotStartZoom)}\" style=\"width:80px;\" />
            </div>

            <div class=\"row lm-add-row\" style=\"margin-top:6px; align-items:center;\">
              <label class=\"small\"><input id=\"enc-auto-refresh\" type=\"checkbox\" ${state.autoRefresh ? "checked" : ""} /> Auto-refresh on move</label>
              <label class=\"small\" style=\"margin-left:10px;\">Max/layer</label>
              <input id=\"enc-max-per\" type=\"number\" min=\"200\" max=\"5000\" step=\"100\" value=\"${state.maxPerLayer}\" style=\"width:100px;\" />
            </div>
          </div>
        </details>

        <details id=\"enc-active-layers-root\" class=\"toolset-group\" ${state.activeLayersOpen ? "open" : ""} style=\"margin-top:8px;\">
          <summary>Active Layers</summary>
          <div id=\"enc-active-layers-list\" class=\"toolset-content\" style=\"max-height:220px; overflow:auto;\">${renderActiveLayers()}</div>
        </details>

        <details id=\"enc-available-layers-root\" class=\"toolset-group\" ${state.availableLayersOpen ? "open" : ""} style=\"margin-top:8px;\">
          <summary>Availble Layers</summary>
          <div class=\"toolset-content\" style=\"max-height:260px; overflow:auto;\">
            <div style=\"padding:4px 2px;\">
              <input id=\"enc-layer-filter\" type=\"text\" value=\"${esc(state.layerFilterText || "")}\" placeholder=\"Filter available layers (e.g. Wreck)\" style=\"width:100%;\" />
            </div>
            <div id=\"enc-layer-checklist\">${renderLayerChecklist()}</div>
          </div>
        </details>

        <details id=\"enc-user-charts-root\" class=\"toolset-group\" ${state.userChartsOpen ? "open" : ""} style=\"margin-top:8px;\">
          <summary>User Charts</summary>
          <div class=\"toolset-content\" style=\"max-height:220px; overflow:auto;\">
            <div class=\"row lm-add-row\" style=\"margin-top:4px; align-items:center;\">
              <input id=\"enc-user-chart-name\" type=\"text\" placeholder=\"New chart name\" style=\"flex:1;\" />
              <button id=\"enc-user-chart-create\" class=\"btn\">Create</button>
            </div>
            <div class=\"small\" style=\"margin-top:6px; opacity:.88;\">Select one chart as active container. Queried/drawn features can be copied into it.</div>
            <div id=\"enc-user-charts-list\" style=\"margin-top:6px;\">${renderUserCharts()}</div>
          </div>
        </details>

        ${state.featureTableDock === "panel" ? `
          <details id="enc-feature-table-root-inline" class="toolset-group" ${state.featureTableOpen ? "open" : ""} style="margin-top:8px;">
            <summary>ENC Feature Table</summary>
            <div class="toolset-content" id="enc-inline-feature-table" style="max-height:420px; overflow:auto;">
              ${renderFeatureTableBody()}
            </div>
          </details>
        ` : ""}

        <details id=\"enc-export-root\" class=\"toolset-group\" ${state.exportOpen ? "open" : ""} style=\"margin-top:8px;\">
          <summary>Export</summary>
          <div class=\"toolset-content\">
            <div class=\"row lm-add-row\" style=\"margin-top:6px; align-items:center;\">
              <label class=\"small\" style=\"min-width:80px;\">AOI</label>
              <select id=\"enc-export-mode\" style=\"flex:1;\">
                <option value=\"bounds\"${state.exportMode === "bounds" ? " selected" : ""}>Screen bounds</option>
                <option value=\"polygon\"${state.exportMode === "polygon" ? " selected" : ""}>User polygon</option>
              </select>
            </div>
            <div class=\"row lm-add-row\" style=\"margin-top:6px;\">
              <button id=\"enc-export-draw\" class=\"btn\" ${state.exportMode === "polygon" ? "" : "disabled"}>${exportDrawMode ? "Drawing..." : "Draw Polygon"}</button>
              <button id=\"enc-export-clear-poly\" class=\"btn\" ${state.exportMode === "polygon" ? "" : "disabled"}>Clear Polygon</button>
              <button id=\"enc-export-run\" class=\"btn\">Export GeoJSON</button>
            </div>
            <div class=\"row lm-add-row\" style=\"margin-top:8px; align-items:center;\">
              <label class=\"small\" style=\"min-width:80px;\">S-57 API</label>
              <input id=\"enc-s57-export-url\" type=\"text\" value=\"${esc(state.s57ExportUrl || "")}\" placeholder=\"http://localhost:8081/v1/export/s57\" style=\"flex:1;\" />
            </div>
            <div class=\"row lm-add-row\" style=\"margin-top:6px; align-items:center;\">
              <label class=\"small\" style=\"min-width:80px;\">Cell</label>
              <input id=\"enc-s57-cell-name\" type=\"text\" value=\"${esc(state.s57CellName || "")}\" maxlength=\"8\" style=\"width:110px;\" />
              <label class=\"small\" style=\"margin-left:8px;\">Edtn</label>
              <input id=\"enc-s57-edition\" type=\"number\" min=\"1\" max=\"999\" step=\"1\" value=\"${esc(state.s57Edition)}\" style=\"width:72px;\" />
              <label class=\"small\" style=\"margin-left:8px;\">Updn</label>
              <input id=\"enc-s57-update-number\" type=\"number\" min=\"0\" max=\"9999\" step=\"1\" value=\"${esc(state.s57UpdateNumber)}\" style=\"width:72px;\" />
              <button id=\"enc-export-s57\" class=\"btn\">Export S-57 (.000)</button>
            </div>
          </div>
        </details>

        <div class=\"small\" style=\"margin-top:8px; min-height:16px;\">${esc(status)}</div>
      </div>
    `;
    bindInlineFeatureTableControls();
  }

  function applyOpacity() {
    for (const rt of layerRuntime.values()) {
      rt.group.eachLayer((l) => {
        if (l?.setStyle) {
          try { l.setStyle({ opacity: state.opacity, fillOpacity: Math.min(0.7, state.opacity * 0.55) }); } catch {}
        }
      });
    }
  }

  function applyLayerStyleToRuntime(serviceId) {
    const id = Number(serviceId);
    const rt = layerRuntime.get(id);
    if (!rt) return;
    const cfg = catalog.find((c) => c.serviceId === id) || rt.cfg;
    if (!cfg) return;
    const style = styleForConfig(cfg);
    rt.group.eachLayer((l) => {
      if (l?.setStyle) {
        try {
          l.setStyle({
            ...style,
            opacity: state.opacity,
            fillOpacity: Math.min(0.7, state.opacity * 0.55),
          });
        } catch {}
      }
    });
    if (cfg.role === "soundings" || cfg.role === "lights" || cfg.typeHint === "point") refreshEnabledLayers({ forceReload: true });
  }

  function applyDepthBandUpdate() {
    state.depthBands = sanitizeDepthBands(state.depthBands);
    persist();
    refreshEnabledLayers({ forceReload: true });
    render();
  }

  function applyLightColorUpdate() {
    state.lightColorMap = sanitizeLightColorMap(state.lightColorMap);
    persist();
    refreshEnabledLayers({ forceReload: true });
    render();
  }

  function applyLightModeUpdate() {
    state.lightSymbologyMode = sanitizeLightSymbologyMode(state.lightSymbologyMode);
    persist();
    refreshEnabledLayers({ forceReload: true });
    render();
  }

  async function onClick(e) {
    if (e.target?.closest?.("#enc-user-chart-create")) {
      const name = String(containerEl?.querySelector?.("#enc-user-chart-name")?.value || "").trim();
      createUserChart(name);
      return;
    }

    const removeChartBtn = e.target?.closest?.("[data-enc-user-chart-remove]");
    if (removeChartBtn) {
      removeUserChart(removeChartBtn.getAttribute("data-enc-user-chart-remove"));
      return;
    }

    if (e.target?.closest?.("#enc-save-user-preset")) {
      const name = String(containerEl?.querySelector?.("#enc-user-preset-name")?.value || "").trim();
      if (!name) {
        setStatus("Preset save error: enter a preset name.");
        return;
      }
      if (saveUserPreset(name)) {
        setStatus(`Saved preset '${name}'.`);
        render();
      }
      return;
    }

    if (e.target?.closest?.("#enc-load-user-preset")) {
      const name = String(containerEl?.querySelector?.("#enc-user-preset-select")?.value || "").trim();
      if (!name) {
        setStatus("Select a saved preset to load.");
        return;
      }
      if (loadUserPreset(name)) {
        setStatus(`Loaded preset '${name}'.`);
      } else {
        setStatus("Preset load error.");
      }
      return;
    }

    if (e.target?.closest?.("#enc-delete-user-preset")) {
      const name = String(containerEl?.querySelector?.("#enc-user-preset-select")?.value || "").trim();
      if (!name) {
        setStatus("Select a saved preset to delete.");
        return;
      }
      if (deleteUserPreset(name)) {
        setStatus(`Deleted preset '${name}'.`);
        render();
      }
      return;
    }

    if (e.target?.closest?.("#enc-export-draw")) {
      if (state.exportMode !== "polygon") return;
      startExportDrawMode();
      return;
    }

    if (e.target?.closest?.("#enc-export-clear-poly")) {
      clearExportAoiDrawing();
      stopExportDrawMode();
      setStatus("Polygon AOI cleared.");
      return;
    }

    if (e.target?.closest?.("#enc-export-run")) {
      updateFromForm();
      exportCurrentFeatures();
      return;
    }

    if (e.target?.closest?.("#enc-export-s57")) {
      updateFromForm();
      await exportCurrentS57();
      return;
    }

    const expandBtn = e.target?.closest?.("[data-enc-layer-expand]");
    if (expandBtn) {
      const id = Number(expandBtn.getAttribute("data-enc-layer-expand"));
      const kind = String(expandBtn.getAttribute("data-enc-expand-kind") || "");
      if (kind === "depth") {
        if (depthBandsExpanded.has(id)) depthBandsExpanded.delete(id);
        else depthBandsExpanded.add(id);
      } else if (kind === "light") {
        if (lightColorsExpanded.has(id)) lightColorsExpanded.delete(id);
        else lightColorsExpanded.add(id);
      }
      render();
      return;
    }

    if (e.target?.closest?.("#enc-load-portrayal-xml")) {
      updateFromForm();
      const url = String(state.portrayalCatalogUrl || "").trim();
      if (!url) {
        setStatus("Portrayal load error: enter a portrayal XML URL.");
        return;
      }
      setStatus("Loading portrayal XML...");
      try {
        const { pack, manifest } = await loadPortrayalPackFromUrl(url, { basePackId: state.portrayalPackId || "enc-v0.1" });
        registerPortrayalPack(pack);
        state.portrayalPackId = sanitizePortrayalPackId(pack.id);
        persist();
        refreshEnabledLayers({ forceReload: true });
        const cls = Array.isArray(manifest?.objectClasses) ? manifest.objectClasses.length : 0;
        setStatus(`Portrayal loaded: ${manifest.title} (${manifest.instructionCount} instructions, ${cls} classes mapped).`);
      } catch (err) {
        setStatus(`Portrayal load error: ${err?.message || String(err)}`);
      }
      render();
      return;
    }

    const removeBtn = e.target?.closest?.("[data-enc-active-remove]");
    if (removeBtn) {
      const id = Number(removeBtn.getAttribute("data-enc-active-remove"));
      const enabled = getEnabledSet();
      enabled.delete(id);
      state.enabledLayerIds = [...enabled];
      state.presetId = "none";
      persist();
      clearLayer(id);
      render();
      return;
    }

    if (e.target?.closest?.("#enc-load-catalog")) {
      loadCatalog();
      return;
    }
    if (e.target?.closest?.("#enc-refresh")) {
      refreshEnabledLayers({ forceReload: true });
      return;
    }
    if (e.target?.closest?.("#enc-clear")) {
      for (const id of [...layerRuntime.keys()]) clearLayer(id);
      clearExportAoiDrawing();
      stopExportDrawMode();
      removedRoot = true;
      try { map.removeLayer(rootGroup); } catch {}
      setStatus("ENC cleared.");
      return;
    }
    if (e.target?.closest?.("#enc-apply-preset")) {
      applyPreset(String(containerEl?.querySelector?.("#enc-preset")?.value || "none"));
    }
  }

  function onInput(e) {
    if (!e.target) return;

    if (e.target.id === "enc-portrayal-url") {
      state.portrayalCatalogUrl = String(e.target.value || "");
      persist();
      return;
    }

    if (e.target.id === "enc-s57-export-url") {
      state.s57ExportUrl = String(e.target.value || "").trim();
      persist();
      return;
    }

    if (e.target.id === "enc-s57-cell-name") {
      state.s57CellName = String(e.target.value || "").trim().slice(0, 8);
      persist();
      return;
    }

    if (e.target.id === "enc-layer-filter") {
      state.layerFilterText = String(e.target.value || "");
      persist();
      const checklistEl = containerEl?.querySelector?.("#enc-layer-checklist");
      if (checklistEl) checklistEl.innerHTML = renderLayerChecklist();
      return;
    }

    const depthColorIdx = e.target.dataset?.encDepthBandColor;
    if (depthColorIdx !== undefined) {
      const idx = Number(depthColorIdx);
      const color = String(e.target.value || "");
      const bands = sanitizeDepthBands(state.depthBands);
      if (bands[idx] && color) {
        bands[idx] = { ...bands[idx], color };
        state.depthBands = bands;
        applyDepthBandUpdate();
      }
      return;
    }

    const depthMaxIdx = e.target.dataset?.encDepthBandMax;
    if (depthMaxIdx !== undefined) {
      const idx = Number(depthMaxIdx);
      const max = Number(e.target.value);
      const bands = sanitizeDepthBands(state.depthBands);
      if (bands[idx] && bands[idx].max !== null && Number.isFinite(max)) {
        bands[idx] = { ...bands[idx], max: Math.max(0, max) };
        state.depthBands = bands;
        applyDepthBandUpdate();
      }
      return;
    }

    const lightColorCode = e.target.dataset?.encLightColorCode;
    if (lightColorCode !== undefined) {
      const code = String(lightColorCode);
      const color = String(e.target.value || "");
      if (/^\d+$/.test(code) && color) {
        state.lightColorMap = { ...sanitizeLightColorMap(state.lightColorMap), [code]: color };
        applyLightColorUpdate();
      }
      return;
    }

    const colorId = e.target.dataset?.encActiveColor;
    if (colorId) {
      const id = Number(colorId);
      setLayerStyleOverride(id, { color: String(e.target.value || "") });
      applyLayerStyleToRuntime(id);
      render();
      return;
    }

    const weightId = e.target.dataset?.encActiveWeight;
    if (weightId) {
      const id = Number(weightId);
      const weight = Number(e.target.value);
      if (Number.isFinite(weight)) {
        setLayerStyleOverride(id, { weight });
        applyLayerStyleToRuntime(id);
      }
      return;
    }

    if (e.target.id === "enc-opacity") {
      updateFromForm();
      applyOpacity();
      render();
      return;
    }
    if (e.target.id === "enc-max-per") {
      updateFromForm();
      render();
      return;
    }

    if (e.target.id === "enc-sounding-dot-zoom") {
      updateFromForm();
      updateZoomIndicator();
      refreshEnabledLayers({ forceReload: true });
      render();
    }
  }

  function onChange(e) {
    const t = e.target;
    if (!t) return;

    const selectUserChartId = t.dataset?.encUserChartSelect;
    if (selectUserChartId) {
      state.selectedUserChartId = String(selectUserChartId);
      persist();
      render();
      renderBottomPanelOnly();
      return;
    }

    const toggleUserChartId = t.dataset?.encUserChartVisible;
    if (toggleUserChartId) {
      const id = String(toggleUserChartId);
      state.userCharts = (state.userCharts || []).map((c) => (String(c.id) === id ? { ...c, visible: !!t.checked } : c));
      persist();
      rebuildUserChartLayers();
      render();
      return;
    }

    const layerToggleId = t.dataset?.encLayerToggle;
    if (layerToggleId) {
      const id = Number(layerToggleId);
      const enabled = getEnabledSet();
      if (t.checked) enabled.add(id);
      else enabled.delete(id);
      state.enabledLayerIds = [...enabled];
      state.presetId = "none";
      persist();
      refreshEnabledLayers({ forceReload: true });
      render();
      return;
    }

    const lineTypeId = t.dataset?.encActiveLineType;
    if (lineTypeId) {
      const id = Number(lineTypeId);
      const lineType = String(t.value || "solid");
      setLayerStyleOverride(id, { lineType });
      applyLayerStyleToRuntime(id);
      render();
      return;
    }

    const lightMode = t.dataset?.encLightMode;
    if (lightMode) {
      state.lightSymbologyMode = sanitizeLightSymbologyMode(t.value);
      applyLightModeUpdate();
      return;
    }

    if (t.id === "enc-point-symbology") {
      state.pointSymbologyMode = sanitizePointSymbologyMode(t.value);
      persist();
      refreshEnabledLayers({ forceReload: true });
      render();
      return;
    }

    if (t.id === "enc-portrayal-pack") {
      state.portrayalPackId = sanitizePortrayalPackId(t.value);
      persist();
      refreshEnabledLayers({ forceReload: true });
      render();
      return;
    }

    if (t.id === "enc-template-library") {
      state.templateLibraryId = sanitizeTemplateLibraryId(t.value);
      state.userChartDraftClass = normalizeClassName(state.userChartDraftClass || "S57_GENERIC");
      persist();
      render();
      renderBottomPanelOnly();
      return;
    }

    if (t.id === "enc-portrayal-url-preset") {
      const v = String(t.value || "");
      if (v !== "__custom") {
        state.portrayalCatalogUrl = v;
        persist();
      }
      render();
      return;
    }

    if (t.id === "enc-export-mode") {
      state.exportMode = String(t.value || "bounds") === "polygon" ? "polygon" : "bounds";
      if (state.exportMode !== "polygon") stopExportDrawMode();
      persist();
      render();
      return;
    }

    if (t.id === "enc-s57-edition") {
      state.s57Edition = Math.max(1, Math.min(999, Number(t.value || state.s57Edition || 1)));
      persist();
      return;
    }

    if (t.id === "enc-s57-update-number") {
      state.s57UpdateNumber = Math.max(0, Math.min(9999, Number(t.value || state.s57UpdateNumber || 0)));
      persist();
      return;
    }

    if (t.id === "enc-user-preset-select") {
      state.selectedUserPreset = String(t.value || "");
      persist();
      return;
    }

    if (t.id === "enc-feature-table-dock") {
      const nextDock = String(t.value || "bottom") === "panel" ? "panel" : "bottom";
      if (state.featureTableDock !== nextDock) {
        state.featureTableDock = nextDock;
        persist();
        syncFeatureTableDock();
        render();
        renderBottomPanelOnly();
      }
      return;
    }

    if (t.id === "enc-auto-refresh" || t.id === "enc-preset") {
      updateFromForm();
      persist();
      render();
    }
  }

  function onToggle(e) {
    const t = e?.target;
    if (!t?.id) return;
    if (t.id === "enc-controls-root") state.controlsOpen = !!t.open;
    if (t.id === "enc-active-layers-root") state.activeLayersOpen = !!t.open;
    if (t.id === "enc-available-layers-root") state.availableLayersOpen = !!t.open;
    if (t.id === "enc-user-charts-root") state.userChartsOpen = !!t.open;
    if (t.id === "enc-export-root") state.exportOpen = !!t.open;
    if (t.id === "enc-feature-table-root-inline") state.featureTableOpen = !!t.open;
    persist();
  }

  function mount(el) {
    if (containerEl) {
      containerEl.removeEventListener("click", onClick);
      containerEl.removeEventListener("input", onInput);
      containerEl.removeEventListener("change", onChange);
      containerEl.removeEventListener("toggle", onToggle, true);
    }
    containerEl = el;
    ensureSelectedUserChart();
    rebuildUserChartLayers();
    render();
    syncFeatureTableDock();
    containerEl.addEventListener("click", onClick);
    containerEl.addEventListener("input", onInput);
    containerEl.addEventListener("change", onChange);
    containerEl.addEventListener("toggle", onToggle, true);

    if (!zoomIndicatorEl) {
      const host = map.getContainer?.() || document.querySelector("#map");
      if (host) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "map-measure-btn enc-zoom-indicator-btn";
        btn.style.position = "absolute";
        btn.style.top = "402px";
        btn.style.right = "12px";
        btn.style.zIndex = "1100";
        btn.style.fontSize = "11px";
        btn.style.fontWeight = "700";
        btn.style.pointerEvents = "none";
        host.appendChild(btn);
        zoomIndicatorEl = btn;
      }
    }
    updateZoomIndicator();

    if (!offMove) {
      const onMove = () => {
        debounceRefresh();
        updateZoomIndicator();
      };
      const onMapClick = (ev) => {
        if (userEditMode) {
          const ll = ev?.latlng;
          if (ll && moveSelectedFeatureVertexTo(ll)) return;
        }
        if (userDrawMode) {
          const ll = ev?.latlng;
          if (!ll) return;
          if (userDrawMode === "point") {
            addFeatureToSelectedChart({
              type: "Feature",
              geometry: { type: "Point", coordinates: [Number(ll.lng), Number(ll.lat)] },
              properties: { objectClass: state.userChartDraftClass || "S57_GENERIC" },
            }, "draw");
            stopUserDrawMode();
            return;
          }
          userDrawDraft.push(L.latLng(ll.lat, ll.lng));
          updateUserDrawGuide();
          renderBottomPanelOnly();
          return;
        }
        if (!exportDrawMode) return;
        const ll = ev?.latlng;
        if (!ll) return;
        exportAoiDraft.push(L.latLng(ll.lat, ll.lng));
        updateExportDraftGraphics();
      };
      const onMapDoubleClick = () => {
        if (userDrawMode) {
          completeUserDraw();
          return;
        }
        if (!exportDrawMode) return;
        if (exportAoiDraft.length < 3) {
          setStatus("Polygon needs at least 3 vertices.");
          return;
        }
        updateExportDraftGraphics();
        stopExportDrawMode();
        setStatus("Polygon AOI ready for export.");
      };
      map.on("moveend", onMove);
      map.on("zoomend", onMove);
      map.on("click", onMapClick);
      map.on("dblclick", onMapDoubleClick);
      offMove = () => {
        map.off("moveend", onMove);
        map.off("zoomend", onMove);
        map.off("click", onMapClick);
        map.off("dblclick", onMapDoubleClick);
      };
    }

    if (!catalog.length) loadCatalog();
  }

  function destroy() {
    if (moveTimer) {
      clearTimeout(moveTimer);
      moveTimer = null;
    }
    if (offMove) {
      try { offMove(); } catch {}
      offMove = null;
    }
    for (const id of [...layerRuntime.keys()]) clearLayer(id);
    try { map.removeLayer(rootGroup); } catch {}
    try { map.removeLayer(userChartsGroup); } catch {}
    userChartsGroup = null;
    if (exportAoiLayer) {
      try { map.removeLayer(exportAoiLayer); } catch {}
      exportAoiLayer = null;
    }
    exportAoiPolyline = null;
    exportAoiPolygon = null;
    exportAoiDraft = [];
    exportDrawMode = false;
    stopUserDrawMode();
    try { map.getContainer().style.cursor = ""; } catch {}
    if (zoomIndicatorEl?.parentNode) {
      try { zoomIndicatorEl.parentNode.removeChild(zoomIndicatorEl); } catch {}
    }
    zoomIndicatorEl = null;
    if (!containerEl) return;
    containerEl.removeEventListener("click", onClick);
    containerEl.removeEventListener("input", onInput);
    containerEl.removeEventListener("change", onChange);
    containerEl.removeEventListener("toggle", onToggle, true);
    containerEl = null;
    if (bottomMounted) {
      app.ui.unmountBottomPanel("enc-charts");
      bottomMounted = false;
    }
  }

  function snapshot() {
    return {
      version: 1,
      state: { ...state },
    };
  }

  function restore(data) {
    const s = data?.state;
    if (!s || typeof s !== "object") return;
    state = {
      ...state,
      ...s,
      opacity: Number.isFinite(Number(s.opacity)) ? Math.max(0.05, Math.min(1, Number(s.opacity))) : state.opacity,
      autoRefresh: s.autoRefresh !== false,
      maxPerLayer: Number.isFinite(Number(s.maxPerLayer)) ? Math.max(200, Math.min(5000, Number(s.maxPerLayer))) : state.maxPerLayer,
      enabledLayerIds: Array.isArray(s.enabledLayerIds) ? s.enabledLayerIds.map((x) => Number(x)).filter((x) => Number.isFinite(x)) : state.enabledLayerIds,
      layerStyles: s.layerStyles && typeof s.layerStyles === "object" ? s.layerStyles : state.layerStyles,
      depthBands: sanitizeDepthBands(s.depthBands || state.depthBands),
      lightColorMap: sanitizeLightColorMap(s.lightColorMap || state.lightColorMap),
      lightSymbologyMode: sanitizeLightSymbologyMode(s.lightSymbologyMode || state.lightSymbologyMode),
      pointSymbologyMode: sanitizePointSymbologyMode(s.pointSymbologyMode || state.pointSymbologyMode),
      portrayalPackId: sanitizePortrayalPackId(s.portrayalPackId || state.portrayalPackId),
      portrayalCatalogUrl: typeof s.portrayalCatalogUrl === "string" ? s.portrayalCatalogUrl : state.portrayalCatalogUrl,
      soundingDotStartZoom: Number.isFinite(Number(s.soundingDotStartZoom)) ? Math.max(6, Math.min(18, Number(s.soundingDotStartZoom))) : state.soundingDotStartZoom,
      controlsOpen: s.controlsOpen !== false,
      activeLayersOpen: s.activeLayersOpen !== false,
      availableLayersOpen: s.availableLayersOpen !== false,
      userChartsOpen: s.userChartsOpen !== false,
      exportOpen: s.exportOpen === true,
      userPresets: Array.isArray(s.userPresets) ? s.userPresets.filter((p) => p && typeof p === "object" && typeof p.name === "string" && p.data && typeof p.data === "object").slice(0, 40) : state.userPresets,
      selectedUserPreset: typeof s.selectedUserPreset === "string" ? s.selectedUserPreset : state.selectedUserPreset,
      exportMode: s.exportMode === "polygon" ? "polygon" : "bounds",
      layerFilterText: typeof s.layerFilterText === "string" ? s.layerFilterText : state.layerFilterText,
      s57ExportUrl: typeof s.s57ExportUrl === "string" && s.s57ExportUrl.trim() ? s.s57ExportUrl.trim() : state.s57ExportUrl,
      s57CellName: typeof s.s57CellName === "string" && s.s57CellName.trim() ? s.s57CellName.trim().slice(0, 8) : state.s57CellName,
      s57Edition: Number.isFinite(Number(s.s57Edition)) ? Math.max(1, Math.min(999, Number(s.s57Edition))) : state.s57Edition,
      s57UpdateNumber: Number.isFinite(Number(s.s57UpdateNumber)) ? Math.max(0, Math.min(9999, Number(s.s57UpdateNumber))) : state.s57UpdateNumber,
      userCharts: sanitizeUserCharts(s.userCharts || state.userCharts),
      selectedUserChartId: typeof s.selectedUserChartId === "string" ? s.selectedUserChartId : state.selectedUserChartId,
      templateLibraryId: sanitizeTemplateLibraryId(s.templateLibraryId || state.templateLibraryId),
      userChartDraftClass: typeof s.userChartDraftClass === "string" && s.userChartDraftClass.trim() ? s.userChartDraftClass.trim().slice(0, 64) : state.userChartDraftClass,
      featureTableDock: s.featureTableDock === "panel" ? "panel" : "bottom",
      featureTableOpen: s.featureTableOpen !== false,
    };
    persist();
    render();
    ensureSelectedUserChart();
    rebuildUserChartLayers();
    syncFeatureTableDock();
    renderBottomPanelOnly();
    refreshEnabledLayers({ forceReload: true });
  }

  return { mount, destroy, snapshot, restore };
}







